
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './App.css';
import ChatAdvisor, { clearTemplateChatHistory } from './ChatAdvisor';
import LessonIframe from './LessonIframe';
import CodePreview from './CodePreview';
import { injectNavGuard } from './navGuard';
import InlineEditor from './InlineEditor';
import ReviewSidebar from './ReviewSidebar';
import DistributorDashboard from './DistributorDashboard';
import AdminPage from './AdminPage';
import MyTasks from './MyTasks';
import LearnerView from './LearnerView';
import CostMonitor from './CostMonitor';
const STORAGE_KEY = 'daily-huddle-board';
const LEGACY_STORAGE_KEY = 'daily-huddle-lesson';
const OPENAI_MODEL_ID = 'gpt-5.1';
const LEGACY_OPENAI_MODELS = ['gpt5', 'openai', 'gpt-5.1-codex', 'gpt-5.1-codex-max'];
const APP_AUTH_STORAGE_KEY = 'app_auth_session';


function App() {
  const iframeRef = useRef(null);
  const lessonFrameRef = useRef(null);
  const menuButtonRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLessonLoading, setIsLessonLoading] = useState(true);
  const [lessonError, setLessonError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateImages, setNewTemplateImages] = useState([]);
  const [newTemplateImagePreviews, setNewTemplateImagePreviews] = useState([]);
  const [newTemplateFile, setNewTemplateFile] = useState(null);
  const [newTemplateFilePath, setNewTemplateFilePath] = useState(null);
  const [newTemplateModel, setNewTemplateModel] = useState(OPENAI_MODEL_ID); // Model selection for template creation
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [creatingTemplateName, setCreatingTemplateName] = useState('');
  const [creatingTemplateDescription, setCreatingTemplateDescription] = useState('');
  const [generationStage, setGenerationStage] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressModalTick, setProgressModalTick] = useState(0);
  const stageTimesRef = useRef({});
  const generationStartRef = useRef(null);
  const templateImageInputRef = useRef(null);
  const templateFileInputRef = useRef(null);
  const assessmentFileInputRef = useRef(null);
  const createInFlightRef = useRef(false); // prevent double submit (e.g. double-click or Strict Mode)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = first confirmation, 2 = second confirmation
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [deleteWorker, setDeleteWorker] = useState(false);
  const [deleteModalDeploymentUrl, setDeleteModalDeploymentUrl] = useState(null);
  const [showCodePreview, setShowCodePreview] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyLessonName, setCopyLessonName] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [galleryViewMode, setGalleryViewMode] = useState(true); // Default to gallery view on load
  const [chatDocked, setChatDocked] = useState(true);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(true);
  const [chatNudge, setChatNudge] = useState(false);
  const [chatTooltip, setChatTooltip] = useState(false);
  const [galleryNudge, setGalleryNudge] = useState(false);
  const [galleryTooltip, setGalleryTooltip] = useState(false);
  const [chatPosition, setChatPosition] = useState({ x: 720, y: 140 });
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  const [isRegeneratingLesson, setIsRegeneratingLesson] = useState(false);
  const [buildCompleteModal, setBuildCompleteModal] = useState(null); // { id, name } when lesson finishes building
  const [iframeNonce, setIframeNonce] = useState(Date.now());
  const selectedTemplateRef = useRef(null); // track current selection for async create (allow opening other apps while creating)
  const prevPendingTemplateIdRef = useRef(null); // tracks previous value to distinguish initial-null from completed-null
  const [showDeployConfirmModal, setShowDeployConfirmModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [deploymentUrl, setDeploymentUrl] = useState(null);
  const [existingDeployment, setExistingDeployment] = useState(null);
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [apiKeysConfig, setApiKeysConfig] = useState({ providers_set: { google: false, openai: false, anthropic: false, deepseek: false }, enabled_models: [], use_user_keys_only: false });
  const [codexModels, setCodexModels] = useState({ models: [], default: '' });
  const [apiKeyInputs, setApiKeyInputs] = useState({ google: '', openai: '', anthropic: '', deepseek: '' });
  const { isAuthenticated, isLoading: authChecking, getAccessTokenSilently, loginWithRedirect, logout, user } = useAuth0();
  const [isEditMode, setIsEditMode] = useState(false);
  const [newTemplateIsAssessment, setNewTemplateIsAssessment] = useState(false);
  const [newTemplateAssessmentSource, setNewTemplateAssessmentSource] = useState('topic');
  const [newTemplateAssessmentTopic, setNewTemplateAssessmentTopic] = useState('');
  const [newTemplateAssessmentTemplateIds, setNewTemplateAssessmentTemplateIds] = useState(''); // comma-separated string
  const [newTemplateQuestionCount, setNewTemplateQuestionCount] = useState('');
  const [newTemplateAssessmentFile, setNewTemplateAssessmentFile] = useState(null);
  const [newTemplateAssessmentFilePath, setNewTemplateAssessmentFilePath] = useState(null);
  const [assessmentFileUploading, setAssessmentFileUploading] = useState(false);
  const [roleReady, setRoleReady] = useState(false)
  const [currentRole, setCurrentRole] = useState(null);
  const [currentToken, setCurrentToken] = useState('');
  const [currentPageId, setCurrentPageId] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [selectedLessonStatus, setSelectedLessonStatus] = useState(null);
  const [showLearnerAssignModal, setShowLearnerAssignModal] = useState(false);
  const [learnerAssignLesson, setLearnerAssignLesson] = useState(null);
  const [currentLessonRole, setCurrentLessonRole] = useState(null);
  const formatDeployTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };
  const [showInlineAlert, setShowInlineAlert] = useState(false);
  const [inlineAlertMessage, setInlineAlertMessage] = useState('');
  const [toasts, setToasts] = useState([]);
  const [showQaModal, setShowQaModal] = useState(false);
  const [qaReportData, setQaReportData] = useState(null);
  const [isQaLoading, setIsQaLoading] = useState(false);
  const [qaRunning, setQaRunning] = useState(false);
  const [fixingIssueId, setFixingIssueId] = useState(null);
  const [fixingTemplateId, setFixingTemplateId] = useState(null);
  const [fixIssueError, setFixIssueError] = useState(null);
  const [batchFixingSection, setBatchFixingSection] = useState(null); // section_key being batch-fixed
  const [batchFixProgress, setBatchFixProgress] = useState(null); // {total, completed}
  const [fixingImprovementId, setFixingImprovementId] = useState(null); // individual improvement being fixed
  const [issuesCollapsed, setIssuesCollapsed] = useState(true); // bottom issues list collapsed by default
  const chatDragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const prevGalleryModeRef = useRef(galleryViewMode);
  const chatMountRef = useRef(false);
  const qaPollingIntervalRef = useRef(null);
  const chatEffectRunsRef = useRef(0);
  const infoBanner = "AI-powered lesson building: ask the Lesson Copilot to research a topic, generate an interactive lesson, and iterate on it. Switch lessons with the selector to manage multiple lessons.";

  const inferApiUrl = () => {
    const envUrl = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    if (envUrl) return envUrl;
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        return 'http://localhost:8011';
      }
      return window.location.origin;
    }
    return '';
  };

  const API_URL = inferApiUrl();
  const API_ORIGIN = (() => {
    try {
      return new URL(API_URL, window.location.origin).origin;
    } catch {
      return '';
    }
  })();
  const API_ORIGIN_ALT = API_ORIGIN ? API_ORIGIN.replace('localhost', '127.0.0.1') : '';
  const IFRAME_URL = `${API_URL}/board.html`;
  const buildIframeSrc = (templateId, nonce = null) => {
    const token = nonce ?? Date.now();
    return `${IFRAME_URL}?template=${encodeURIComponent(templateId)}&_t=${token}&preview=true`;
  };
  const BRAND_LOGOS = {
    arbiteria: `${API_URL}/brand_logos/Arbiteria_withTL.png`,
    zero2ai: `${API_URL}/brand_logos/zero2AI._logomain.svg`
  };

  const handleOpenQaFeedback = async () => {
    if (!selectedTemplate) return;
    if (fixingIssueId !== null && fixingTemplateId === selectedTemplate && qaReportData && !qaReportData.error) {
      setShowQaModal(true);
      return;
    }
    setQaReportData(null);
    setIsQaLoading(true);
    setShowQaModal(true);
    try {
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/qa-report`, withAuthFetchOptions());
      const data = await response.json();
      if (data.success && data.report) {
        setQaReportData({ status: data.status, ...data.report });
      } else {
        setQaReportData({ status: data.status, error: data.error || "Report not available yet or evaluation failed." });
      }
    } catch (err) {
      console.error("Error fetching QA report:", err);
      setQaReportData({ status: 'error', error: "Failed to fetch report." });
    } finally {
      setIsQaLoading(false);
    }
  };

    // Poll /qa-report every 5s until the backend finishes and returns status "ready".
  // Max ~3 minutes (36 attempts) before giving up.
  const startQaPolling = (templateId) => {
    // Clear any existing interval so only one ever runs at a time → single toast
    if (qaPollingIntervalRef.current) {
      clearInterval(qaPollingIntervalRef.current);
      qaPollingIntervalRef.current = null;
    }
    const MAX_ATTEMPTS = 36;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateId)}/qa-report`, withAuthFetchOptions());
        const data = await res.json();
        if (data.success && data.report) {
          clearInterval(interval);
          qaPollingIntervalRef.current = null;
          setIsQaLoading(false);
          setQaRunning(false);
          showToast('✅ QA evaluation complete! Click the QA button to view the report.', 'success', 30000);
          setQaReportData({ status: data.status, ...data.report });
          return;
        }
        if (data.status !== 'generating') {
          clearInterval(interval);
          qaPollingIntervalRef.current = null;
          setIsQaLoading(false);
          setQaRunning(false);
          setQaReportData({ status: data.status, error: data.error || "Evaluation finished but no report found." });
          return;
        }
      } catch (_) {
        // Network hiccup — keep polling
      }
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(interval);
        qaPollingIntervalRef.current = null;
        setIsQaLoading(false);
        setQaRunning(false);
        setQaReportData({ status: 'error', error: "Evaluation is taking longer than expected. Please refresh to check." });
      }
    }, 5000);
    qaPollingIntervalRef.current = interval;
  };

  const getIframeOrigin = () => {
    const src = iframeRef.current?.src;
    if (!src) return API_ORIGIN || API_ORIGIN_ALT || '*';
    try {
      return new URL(src, window.location.origin).origin;
    } catch {
      return API_ORIGIN || API_ORIGIN_ALT || '*';
    }
  };

  const loadStoredAppSession = () => {
    try {
      const raw = localStorage.getItem(APP_AUTH_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };

  const persistAppSession = (session) => {
    try {
      if (!session) {
        localStorage.removeItem(APP_AUTH_STORAGE_KEY);
        return;
      }
      localStorage.setItem(APP_AUTH_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // ignore storage errors
    }
  };

  const getAuthSessionToken = () => {
    if (currentToken) return currentToken;
    const session = loadStoredAppSession();
    return typeof session?.session_token === 'string' ? session.session_token.trim() : '';
  };

  const withAuthFetchOptions = (options = {}) => {
    const merged = { ...options, credentials: 'include' };
    const headers = { ...(options.headers || {}) };
    const token = getAuthSessionToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    merged.headers = headers;
    return merged;
  };

  // Generate or retrieve session ID for ownership tracking
  const getSessionId = () => {
    let sessionId = localStorage.getItem('lesson_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('lesson_session_id', sessionId);
    }
    return sessionId;
  };

  const [templateOwnership, setTemplateOwnership] = useState({});

  const syncTemplateOwnershipFromList = (templates = []) => {
    const updates = {};
    (templates || []).forEach((t) => {
      if (!t || !t.id) return;
      if (typeof t.is_owner !== 'boolean' && typeof t.can_edit !== 'boolean') return;
      updates[t.id] = {
        is_owner: typeof t.is_owner === 'boolean' ? t.is_owner : !!t.can_edit,
        can_edit: typeof t.can_edit === 'boolean' ? t.can_edit : !!t.is_owner,
        owner_id: t.owner_id ?? null,
        user_id: t.user_id ?? null,
      };
    });
    if (Object.keys(updates).length > 0) {
      setTemplateOwnership((prev) => ({ ...prev, ...updates }));
    }
  };

  const checkTemplateOwnership = async (templateId) => {
    if (!templateId) return false;

    const sessionId = getSessionId();
    try {
      const response = await fetch(
        `${API_URL}/board/templates/${encodeURIComponent(templateId)}/ownership?owner_id=${encodeURIComponent(sessionId)}`,
        withAuthFetchOptions()
      );
      if (response.ok) {
        const data = await response.json();
        setTemplateOwnership(prev => ({
          ...prev,
          [templateId]: data
        }));
        return data.is_owner;
      }
    } catch (error) {
      console.error('Error checking template ownership:', error);
    }
    return false;
  };

  useEffect(() => {
    if (!showProgressModal) return;
    const t = setInterval(() => setProgressModalTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [showProgressModal]);

  useEffect(() => {
    document.title = 'LessonGen | AI Lesson Builder';
    // Intentional one-time setup for title
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, isLessonLoading]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_API_AUDIENCE,
          }
        });
        setCurrentToken(token);
        // Extract role from Auth0 token namespace
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const roles = tokenPayload['https://lessongen/roles'] || [];
          if (roles.length === 0) {
            // No role assigned — show error, don't let them in
            setCurrentRole(null);
            console.warn('[Auth0] User has no role assigned:', user?.email);
          } else {
            const cleanRoles = roles.map(r => r.trim());
            const primaryRole = cleanRoles.includes('manager') ? 'manager'
              : cleanRoles.includes('organizer') ? 'organizer'
              : cleanRoles.includes('final_approver') ? 'final_approver'
              : cleanRoles.includes('reviewer') ? 'reviewer'
              : cleanRoles.includes('distributor') ? 'distributor'
              : cleanRoles.includes('creator') ? 'creator'
              : cleanRoles.includes('learner') ? 'learner'
              : cleanRoles[0];
            setCurrentRole(primaryRole);
            console.log('[Auth0] role:', primaryRole, 'all roles:', cleanRoles, 'user:', user?.email);
            setRoleReady(true);
            console.log('[Auth0] roleReady set, role:', primaryRole);
          }
        } catch {
          setCurrentRole(null);
        }
      } catch (e) {
        console.error('Failed to get Auth0 token:', e);
        // If Auth0 says login is required (e.g. refresh token expired), redirect cleanly
        // For other errors (network, backend down), don't force logout — just retry on next render
        if (e?.error === 'login_required' || e?.error === 'consent_required') {
          loginWithRedirect({
            authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE }
          });
        }
        // All other errors: leave roleReady=false, user stays on loading screen
        // They can refresh the page manually — don't force a logout loop
      }
    };
    fetchToken();
  }, [isAuthenticated, user, getAccessTokenSilently]);

 

  // Chat floating drag handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!chatDragRef.current.dragging) return;
      setChatPosition({
        x: Math.max(8, chatDragRef.current.offsetX + e.clientX),
        y: Math.max(8, chatDragRef.current.offsetY + e.clientY),
      });
    };
    const handleMouseUp = () => {
      chatDragRef.current.dragging = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Nudge chat icon when minimized
  useEffect(() => {
    chatEffectRunsRef.current += 1;
    if (!chatMountRef.current || chatEffectRunsRef.current <= 2) {
      chatMountRef.current = true;
      return;
    }
    if (chatMinimized) {
      setChatNudge(true);
      setChatTooltip(true);
      const t1 = setTimeout(() => setChatNudge(false), 1800);
      const t2 = setTimeout(() => setChatTooltip(false), 2800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    // clear any lingering tooltip when opened
    setChatNudge(false);
    setChatTooltip(false);
  }, [chatMinimized]);

  // Keep ref in sync so create-success handler knows if user switched to another lesson
  useEffect(() => {
    selectedTemplateRef.current = selectedTemplate;
  }, [selectedTemplate]);

  // Nudge gallery icon when entering edit mode (gallery -> canvas)
  useEffect(() => {
    if (prevGalleryModeRef.current === true && galleryViewMode === false) {
      setChatDocked(true);
      setChatCollapsed(false);
      setChatMinimized(true);
      setGalleryNudge(true);
      setGalleryTooltip(true);
      const t1 = setTimeout(() => setGalleryNudge(false), 1800);
      const t2 = setTimeout(() => setGalleryTooltip(false), 2800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    prevGalleryModeRef.current = galleryViewMode;
  }, [galleryViewMode]);


  // Get state from iframe using postMessage (cross-origin safe)
  const getStateFromIframe = (callback, retries = 3) => {
    const iframe = iframeRef.current;
    if (!iframe) {
      console.warn('Iframe ref is not available');
      callback(null);
      return;
    }

    // Check if iframe is loaded
    try {
      if (!iframe.contentWindow) {
        console.warn('Iframe contentWindow is not available');
        if (retries > 0) {
          // Retry after a short delay
          setTimeout(() => getStateFromIframe(callback, retries - 1), 500);
          return;
        }
        callback(null);
        return;
      }
    } catch (e) {
      console.warn('Cannot access iframe contentWindow (cross-origin):', e);
      // This is expected for cross-origin, continue with postMessage
    }

    let messageReceived = false;
    const handleMessage = (event) => {
      // Accept messages from backend origin
      const isBackendOrigin = event.origin === API_ORIGIN || event.origin === API_ORIGIN_ALT;
      
      if (!isBackendOrigin) {
        return;
      }
      
      if (event.data && event.data.type === 'STATE_DATA') {
        messageReceived = true;
        window.removeEventListener('message', handleMessage);
        callback(event.data.payload);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Request state from iframe
    try {
      iframe.contentWindow.postMessage({ type: 'GET_STATE' }, getIframeOrigin());
    } catch (e) {
      console.error('Error sending postMessage to iframe:', e);
      window.removeEventListener('message', handleMessage);
      if (retries > 0) {
        // Retry after a short delay
        setTimeout(() => getStateFromIframe(callback, retries - 1), 500);
        return;
      }
      callback(null);
      return;
    }
    
    // Timeout after 3 seconds (increased from 1 second)
    setTimeout(() => {
      if (!messageReceived) {
        window.removeEventListener('message', handleMessage);
        if (retries > 0) {
          // Retry
          console.log(`Retrying getStateFromIframe, ${retries} retries left`);
          getStateFromIframe(callback, retries - 1);
        } else {
          console.warn('Timeout waiting for STATE_DATA from iframe');
          callback(null);
        }
      }
    }, 3000);
  };

  const handleSave = () => {
    const iframe = iframeRef.current;
    if (!iframe) {
      setSaveStatus('Error: Iframe not available');
      setTimeout(() => setSaveStatus(''), 2000);
      return;
    }

        // Check if iframe is still loading
        if (isLessonLoading) {
          setSaveStatus('Please wait for lesson to load...');
          setTimeout(() => setSaveStatus(''), 2000);
          return;
        }

    setSaveStatus('Saving...');
    getStateFromIframe((data) => {
      if (data) {
        const payload = { ...data, savedAt: new Date().toISOString() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        setSaveStatus('Saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        setSaveStatus('Error: Could not access iframe. Please try again.');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    });
  };

  const handleClear = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    // Clear state in iframe via postMessage
    const iframe = iframeRef.current;
    if (iframe) {
      const emptyData = { inputs: [], textareas: [] };
      iframe.contentWindow.postMessage({
        type: 'RESTORE_STATE',
        payload: emptyData
      }, getIframeOrigin());
    }
    setSaveStatus('Cleared');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

    const _pollForDeploy = async (templateId) => {
    const maxPolls = 60; // 3 minutes at 3s intervals
    for (let i = 0; i < maxPolls; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await fetch(
          `${API_URL}/board/templates/${encodeURIComponent(templateId)}/deploy-status`,
          withAuthFetchOptions({})
        );
        if (!res.ok) continue;
        const data = await res.json();
        if (data.status === 'success') return { success: true, url: data.deployment_url };
        if (data.status === 'error') return { success: false, error: data.error };
        // status === 'deploying' → keep polling
      } catch (e) { /* network blip, keep trying */ }
    }
    return { success: false, error: 'Deployment timed out. It may have succeeded — check Cloudflare Pages.' };
  };

  const handleDeploy = async () => {
    if (!selectedTemplate) return;
    setIsDeploying(true);
    setDeploymentStatus(null);
    setDeploymentUrl(null);
    try {
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/deploy`, withAuthFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }));
      // Read as text first to avoid "body stream already read" if response is non-JSON
      const rawText = await response.text();
      let data = null;
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        throw new Error(`Deploy API returned non-JSON (${response.status}): ${rawText.slice(0, 200)}`);
      }
      // Backward compatibility: older backend returned [payload, status_code]
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') data = data[0];

      if (data?.status === 'deploying') {
        // Background deploy started — poll for result
        const result = await _pollForDeploy(selectedTemplate);
        if (result.success) {
          setDeploymentStatus('success');
          setDeploymentUrl(result.url);
          if (result.url) setExistingDeployment({ deployment_url: result.url, deployed_at: new Date().toISOString() });
        } else {
          setDeploymentStatus('error');
          setDeploymentUrl(null);
          alert(`Deployment failed: ${result.error}`);
        }
      } else if (data?.success) {
        // Immediate success (file unchanged, returned cached result)
        setDeploymentStatus('success');
        const url = data.deployment_url || data.production_url;
        setDeploymentUrl(url);
        if (data.deployment) setExistingDeployment(data.deployment);
        else if (url) setExistingDeployment({ deployment_url: url, deployed_at: new Date().toISOString() });
      } else {
        setDeploymentStatus('error');
        setDeploymentUrl(null);
        const errorMsg = data?.error || data?.message || `HTTP ${response.status}`;
        alert(`Deployment failed: ${errorMsg}`);
      }
    } catch (error) {
      setDeploymentStatus('error');
      setDeploymentUrl(null);
      alert(`Error deploying lesson: ${error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  const handleDeployClick = () => setShowDeployConfirmModal(true);

  const handleDeployConfirm = () => {
    setShowDeployConfirmModal(false);
    setShowDeployModal(true);
    handleDeploy();
  };

  const openInlineAlert = (message) => {
    setInlineAlertMessage(message);
    setShowInlineAlert(true);
  };

  const showToast = (message, type = 'info', duration = 5000) => {
  const id = Date.now() + Math.random();
  setToasts(prev => [...prev, { id, message, type }]);
  setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
};


// Load available templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      
      if (!isAuthenticated) return;
      if (!currentRole) return;
      console.log('[loadTemplates] currentRole:', currentRole);
      try {
        console.log('Loading templates from:', `${API_URL}/board/templates`);
        const response = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Templates response:', data);
        let templates = data.templates || [];

        // Decode role directly from token to avoid stale state
        let tokenRole = null;
        try {
          const tok = withAuthFetchOptions().headers?.Authorization?.split(' ')[1];
          if (tok) {
            const tp = JSON.parse(atob(tok.split('.')[1]));
            const r = (tp['https://lessongen/roles'] || []).map(x => x.trim());
            tokenRole = r.includes('manager') ? 'manager'
              : r.includes('organizer') ? 'organizer'
              : r.includes('final_approver') ? 'final_approver'
              : r.includes('reviewer') ? 'reviewer'
              : r.includes('distributor') ? 'distributor'
              : r.includes('creator') ? 'creator' : null;
          }
        } catch {}
        console.log('[loadTemplates] tokenRole:', tokenRole);

        if (['creator', 'reviewer', 'final_approver', 'distributor', 'learner'].includes(tokenRole)) {
          const assignedRes = await fetch(`${API_URL}/lessons/assigned`, withAuthFetchOptions());
          const assignedData = await assignedRes.json();
          if (assignedData.success) {
            const assignedIds = assignedData.lessons.map(l => l.template_id);
            if (tokenRole === 'creator') {
              // Creator sees lessons they own OR are assigned to
              const ownedIds = templates.filter(t => t.is_owner).map(t => t.id);
              const allIds = [...new Set([...ownedIds, ...assignedIds])];
              templates = templates.filter(t => allIds.includes(t.id));
            } else {
              // Reviewer/approver/distributor only see assigned lessons
              templates = templates.filter(t => assignedIds.includes(t.id));
            }
          }
        }
        // Organizer: backend already filtered templates to their org — no extra frontend filter needed

        setAvailableTemplates(templates);
        syncTemplateOwnershipFromList(templates);
        if (templates.length > 0) {
          setSelectedTemplate(prevTemplate => {
            const currentExists = templates.find(t => t.id === prevTemplate);
            return currentExists ? prevTemplate : templates[0].id;
          });
        } else {
          setSelectedTemplate(null);
        }

        // Restore in-progress generation that was interrupted by a page refresh
        try {
          const stored = localStorage.getItem('_pending_gen');
          if (stored) {
            const { id: pendingId, name: pendingName } = JSON.parse(stored);
            const alreadyPresent = templates.find(t => t.id === pendingId);
            if (alreadyPresent) {
              // Generation finished while we were away — clear the flag
              localStorage.removeItem('_pending_gen');
            } else {
              const sr = await fetch(`${API_URL}/board/templates/${encodeURIComponent(pendingId)}/create-status`, withAuthFetchOptions({}));
              if (sr.ok) {
                const sd = await sr.json();
                if (sd.status === 'creating') {
                  // Add placeholder card back and resume polling
                  setAvailableTemplates(prev => [...prev, { id: pendingId, name: pendingName }]);
                  setPendingTemplateId(pendingId);
                  selectedTemplateRef.current = pendingId;
                  // Resume polling in background
                  (async () => {
                    const maxPolls = 600;
                    let finalStatus = null;
                    for (let i = 0; i < maxPolls; i++) {
                      await new Promise(r => setTimeout(r, 3000));
                      try {
                        const pr = await fetch(`${API_URL}/board/templates/${encodeURIComponent(pendingId)}/create-status`, withAuthFetchOptions({}));
                        if (!pr.ok) continue;
                        const pd = await pr.json();
                        if (pd.stage) {
                          if (!stageTimesRef.current[pd.stage]) stageTimesRef.current[pd.stage] = Date.now();
                          setGenerationStage(pd.stage);
                        }
                        if (pd.status === 'regenerating') {
                          stageTimesRef.current = {}; generationStartRef.current = Date.now();
                          setIsRegeneratingLesson(true); setGenerationStage('stage1_domain_playbook');
                          stageTimesRef.current['stage1_domain_playbook'] = Date.now();
                        }
                        if (pd.status === 'done' || pd.status === 'error') { setGenerationStage(null); setShowProgressModal(false); finalStatus = pd.status; break; }
                      } catch (_) {}
                    }
                    setIsRegeneratingLesson(false);
                    // Reload templates to pick up the completed lesson
                    try {
                      const tr = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
                      const td = await tr.json();
                      if (td.templates?.length > 0) {
                        setAvailableTemplates(td.templates);
                        syncTemplateOwnershipFromList(td.templates);
                      }
                    } catch (_) {}
                    setPendingTemplateId(null);
                    localStorage.removeItem('_pending_gen');
                    if (finalStatus === 'done') {
                      setBuildCompleteModal({ id: pendingId, name: pendingName || pendingId });
                    }
                  })();
                } else {
                  // Already done/error — clear flag and refresh list
                  localStorage.removeItem('_pending_gen');
                  if (sd.status === 'done') {
                    const tr = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
                    const td = await tr.json();
                    if (td.templates?.length > 0) {
                      setAvailableTemplates(td.templates);
                      syncTemplateOwnershipFromList(td.templates);
                    }
                  }
                }
              } else {
                localStorage.removeItem('_pending_gen');
              }
            }
          }
        } catch (_) { localStorage.removeItem('_pending_gen'); }

      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };
    loadTemplates();
  }, [API_URL, isAuthenticated, roleReady]);

  useEffect(() => {
    if (!selectedTemplate || !currentToken) return;

    // Fetch review status
    fetch(`${API_URL}/lesson/templates/${selectedTemplate}/review/status`, {
      headers: { Authorization: `Bearer ${currentToken}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) setSelectedLessonStatus(data.review?.status);
      })
      .catch(() => {});

    // Fetch current user's role for this specific lesson
    fetch(`${API_URL}/lesson/templates/${selectedTemplate}/my-role`, {
      headers: { Authorization: `Bearer ${currentToken}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setCurrentLessonRole(data.role);
          console.log('[LessonRole] template:', selectedTemplate, 'role:', data.role);
        }
      })
      .catch(() => {
        // Fallback to global role
        setCurrentLessonRole(currentRole);
      });

  }, [selectedTemplate, currentToken, API_URL]);

  const BYOK_STORAGE_KEYS = { google: 'appstorm_byok_google', openai: 'appstorm_byok_openai', anthropic: 'appstorm_byok_anthropic', deepseek: 'appstorm_byok_deepseek' };
const getStoredApiKeys = () => ({
  google: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.google)) || '',
  openai: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.openai)) || '',
  anthropic: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.anthropic)) || '',
  deepseek: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.deepseek)) || '',
});
const getProvidersSetFromStorage = () => {
  const k = getStoredApiKeys();
  return {
    google: !!(k.google && k.google.trim()),
    openai: !!(k.openai && k.openai.trim()),
    anthropic: !!(k.anthropic && k.anthropic.trim()),
    deepseek: !!(k.deepseek && k.deepseek.trim()),
  };
};

  const fetchKeysConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/board/keys`);
      if (res.ok) {
        const data = await res.json();
        const providers_set = getProvidersSetFromStorage();
        setApiKeysConfig({
          providers_set,
          enabled_models: [],
          use_user_keys_only: !!data.use_user_keys_only,
        });
      }
    } catch (e) {
      console.error('Error fetching keys config:', e);
      setApiKeysConfig(prev => ({ ...prev, providers_set: getProvidersSetFromStorage() }));
    }
  };

  const fetchCodexModels = async () => {
    try {
      const res = await fetch(`${API_URL}/board/codex-models`);
      if (res.ok) {
        const data = await res.json();
        setCodexModels(data);
        // Pre-select the server default if nothing is selected yet
        if (data.default) setNewTemplateModel(prev => prev || data.default);
      }
    } catch (e) {
      console.error('Error fetching codex models:', e);
    }
  };

  useEffect(() => {
    if (!API_URL || !isAuthenticated) return;
    setApiKeyInputs(getStoredApiKeys());
    fetchKeysConfig();
    fetchCodexModels();
  }, [API_URL, isAuthenticated]);

  const normalizeModelId = (candidate) => {
    if (!candidate) return '';
    const clean = candidate.trim().toLowerCase();
    if (LEGACY_OPENAI_MODELS.includes(clean)) return OPENAI_MODEL_ID;
    return clean;
  };
  const modelToProvider = {
  gemini: 'google',
  [OPENAI_MODEL_ID]: 'openai',
  gpt5: 'openai',
  'claude-sonnet-4-5': 'anthropic',
  'claude-opus-4-6': 'anthropic',
  'deepseek-v4-flash': 'deepseek',
  'deepseek-v4-pro': 'deepseek',
  deepseek: 'deepseek',
};
  const isModelEnabled = (modelId) => {
    const normalized = normalizeModelId(modelId) || modelId;
    if (!apiKeysConfig.use_user_keys_only) return true;
    const provider = modelToProvider[normalized] || modelToProvider[modelId];
    return provider && apiKeysConfig.providers_set?.[provider];
  };
  const enabledModelIds = [
  'gemini', OPENAI_MODEL_ID, 'claude-sonnet-4-5', 'claude-opus-4-6',
  'deepseek-v4-flash', 'deepseek-v4-pro',
    ...(process.env.REACT_APP_SHOW_OLLAMA_MODELS === 'true' ? [
  'ollama-qwen3.5-397b', 'ollama-kimi-k2.5',
  'ollama-qwen3-coder-next', 'ollama-gemma4-31b'
] : [])
  ].filter(isModelEnabled);
  useEffect(() => {
    if (apiKeysConfig.use_user_keys_only && enabledModelIds.length && !enabledModelIds.includes(newTemplateModel)) {
      setNewTemplateModel(enabledModelIds[0]);
    }
  }, [apiKeysConfig.use_user_keys_only, enabledModelIds.join(','), newTemplateModel]);

  const saveApiKeys = () => {
    if (typeof localStorage !== 'undefined') {
      if (apiKeyInputs.google != null) localStorage.setItem(BYOK_STORAGE_KEYS.google, apiKeyInputs.google || '');
      if (apiKeyInputs.openai != null) localStorage.setItem(BYOK_STORAGE_KEYS.openai, apiKeyInputs.openai || '');
      if (apiKeyInputs.deepseek != null) localStorage.setItem(BYOK_STORAGE_KEYS.deepseek, apiKeyInputs.deepseek || '');
    }
    setApiKeysConfig(prev => ({ ...prev, providers_set: getProvidersSetFromStorage() }));
    setShowKeysModal(false);
  };

  // Check ownership when template changes
  useEffect(() => {
    if (!pendingTemplateId) {
      // Only wipe localStorage when transitioning away from an active generation,
      // not on the initial mount where pendingTemplateId starts as null.
      if (prevPendingTemplateIdRef.current !== null) {
        localStorage.removeItem('_pending_gen');
      }
      prevPendingTemplateIdRef.current = null;
      return;
    }
    prevPendingTemplateIdRef.current = pendingTemplateId;
  }, [pendingTemplateId]);

  useEffect(() => {
    if (selectedTemplate) {
      if (pendingTemplateId && selectedTemplate === pendingTemplateId) {
        return;
      }
      checkTemplateOwnership(selectedTemplate);
    }
  }, [selectedTemplate, pendingTemplateId]);

  // Fetch existing deployment info when template changes
  useEffect(() => {
        const fetchDeploymentInfo = async () => {
      if (!selectedTemplate) return;
      if (pendingTemplateId && selectedTemplate === pendingTemplateId) {
        return;
      }
          try {
            const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/deployment`);
        const data = await response.json();
        if (data.success && data.deployment) {
          setExistingDeployment(data.deployment);
        } else {
          setExistingDeployment(null);
        }
      } catch (error) {
        console.error('Error fetching deployment info:', error);
        setExistingDeployment(null);
      } finally {
        setDeploymentStatus(null);
        setDeploymentUrl(null);
        setShowDeployModal(false);
        setShowDeployConfirmModal(false);
      }
    };
    fetchDeploymentInfo();
  }, [selectedTemplate, API_URL, pendingTemplateId]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  // Handle iframe load events to show/hide loading indicator and catch errors
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      // If iframe ref is not ready, try again after a short delay
      const retryTimeout = setTimeout(() => {
        if (iframeRef.current) {
          setIsLessonLoading(false);
        }
      }, 1000);
      return () => clearTimeout(retryTimeout);
    }

    const handleLoad = () => {
      console.log('Iframe load event fired, hiding loading indicator');
      // Clear timeout if iframe loads successfully
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      // Hide loading immediately when load event fires
      // postMessage is just a bonus confirmation, but load event is reliable
      setIsLessonLoading(false);
      setLessonError(null); // Clear any previous errors on successful load
      // Inject lightweight nav guard to keep pagination responsive
      try {
        const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
        if (doc) {
          injectNavGuard(doc);
        }
      } catch (err) {
        console.warn('Nav guard injection skipped:', err);
      }
    };

        const handleError = (event) => {
          console.error('Iframe load error:', event);
          setIsLessonLoading(false);
          setLessonError('Failed to load lesson. Please check if the backend server is running.');
        };

    // Set loading initially
    setIsLessonLoading(true);
    setLessonError(null);

    // Listen for load and error events
    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    // Also check if iframe is already loaded (with retry for cross-origin)
    const checkIfLoaded = () => {
      try {
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
          console.log('Iframe already loaded (same-origin)');
          handleLoad();
          return true;
        }
      } catch (e) {
        // Cross-origin, can't access directly - that's expected
        console.log('Cannot access iframe document (cross-origin - expected)');
        // For cross-origin, we rely on postMessage or load event
        // Check if iframe src has loaded by checking if it's not blank
        if (iframe.src && iframe.src !== 'about:blank') {
          // Give it a moment, then check if load event fires
          setTimeout(() => {
            // If still loading after 2 seconds, try to trigger load check
            if (isLessonLoading) {
              console.log('Iframe src set but no load event yet, waiting for postMessage...');
            }
          }, 2000);
        }
      }
      return false;
    };
    
    // Check immediately
    if (!checkIfLoaded()) {
      // Also check after a short delay
      setTimeout(checkIfLoaded, 500);
    }

    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Also check for errors after a timeout
    loadingTimeoutRef.current = setTimeout(() => {
      // If still loading after 15 seconds, show error
      console.log('Loading timeout reached, checking iframe state');
      // Check if iframe actually loaded but message wasn't received
      if (iframeRef.current) {
        try {
          // Try to access iframe to see if it loaded
          const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
          if (iframeDoc && iframeDoc.readyState === 'complete') {
            console.log('Iframe loaded but message not received, hiding loading');
            setIsLessonLoading(false);
            return;
          }
        } catch (e) {
          // Cross-origin, can't access - that's expected
          console.log('Cannot access iframe (cross-origin), checking network');
        }
      }
      
      // Check if backend is reachable
      fetch(`${API_URL}/board/last-modified`)
        .then(() => {
          // Backend is reachable, iframe might just be slow
          setLessonError('Artifacts are taking longer than expected to load. The backend server is running. Please try refreshing or check the browser console for errors.');
        })
        .catch(() => {
          // Backend is not reachable
          setLessonError('Cannot connect to backend server. Please ensure the backend server is running and REACT_APP_API_URL is set.');
        });
      
      setIsLessonLoading(false);
    }, 15000); // Increased to 15 seconds

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [API_URL]);

  // Listen for IFRAME_LOADED message from LessonIframe to hide loading
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('Received postMessage:', event.data, 'from origin:', event.origin);
      
      // Accept messages from backend origin (iframe) or same origin
      const isBackendOrigin = event.origin === API_ORIGIN || 
                              event.origin === API_ORIGIN_ALT ||
                              event.origin === window.location.origin ||
                              event.origin === '*';
      
      if (!isBackendOrigin && event.origin !== '*') {
        console.log('Ignoring message from origin:', event.origin);
        return;
      }
      
      if (event.data && event.data.type === 'IFRAME_LOADED') {
        console.log('IFRAME_LOADED message received, hiding loading indicator');
        // Clear timeout if message received
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        setLessonError(null); // Clear errors on successful load
      } else if (event.data && event.data.type === 'IFRAME_ERROR') {
        console.log('IFRAME_ERROR message received:', event.data.error);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        const details = event.data.details || {};
        const locationBits = [];
        if (details.source) locationBits.push(details.source);
        if (details.line) locationBits.push(`line ${details.line}`);
        if (details.column) locationBits.push(`col ${details.column}`);
        const locationText = locationBits.length ? ` (${locationBits.join(', ')})` : '';
        const stackText = details.stack ? `\n${details.stack}` : '';
        setLessonError((event.data.error || 'Failed to load artifacts content.') + locationText + stackText);
      } else if (event.data && event.data.type === 'STATE_RESTORED') {
        console.log('STATE_RESTORED message received, iframe is loaded');
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        setLessonError(null);
      } else if (event.data && event.data.type === 'PAGE_CHANGE') {
        setCurrentPageId(event.data.pageId);
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('Added message listener for IFRAME_LOADED');
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleTemplateChange = (templateId) => {
    if (templateId === 'add-new') {
      setShowAddTemplateModal(true);
      return;
    }
    setSelectedTemplate(templateId);
    const nonce = Date.now();
    setIframeNonce(nonce);
    // Reload iframe with new template
    if (iframeRef.current) {
      setIsLessonLoading(true);
      const newSrc = buildIframeSrc(templateId, nonce);
      iframeRef.current.src = newSrc;
    }
  };

      const handleDeleteClick = async (templateId) => {
    const template = availableTemplates.find(t => t.id === templateId);
    setTemplateToDelete({ id: templateId, name: template ? template.name : templateId });
    setDeleteStep(1);
    setDeleteWorker(false);
    setDeleteModalDeploymentUrl(existingDeployment?.deployment_url || null);
    setShowDeleteModal(true);
    // Always fetch fresh — existingDeployment may be null for newly generated templates
    try {
      const res = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateId)}/deployment`);
      const d = await res.json();
      const url = d?.deployment?.deployment_url || null;
      if (url) setDeleteModalDeploymentUrl(url);
    } catch (_) {}
  };

  const handleCopyClick = (templateId) => {
    const template = availableTemplates.find(t => t.id === templateId);
    const baseName = template ? template.name : templateId;
    setCopyLessonName(`${baseName} Copy`);
    setShowCopyModal(true);
  };

      const handleCopyLesson = async () => {
        if (!copyLessonName.trim()) {
          alert('Please enter a name for the copied lesson');
          return;
        }

        setIsCopying(true);
            try {
                  const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/copy`, withAuthFetchOptions({
                method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              new_name: copyLessonName.trim()
            }),
          }));

          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(data.error || data.detail || 'Copy failed');
          }

              const templatesResponse = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
          const templatesData = await templatesResponse.json();
          if (templatesData.templates && templatesData.templates.length > 0) {
            setAvailableTemplates(templatesData.templates);
          syncTemplateOwnershipFromList(templatesData.templates);
          }

      setSelectedTemplate(data.template_id);
      setShowCopyModal(false);
      setCopyLessonName('');
      clearTemplateChatHistory(data.template_id);
      checkTemplateOwnership(data.template_id);

      if (iframeRef.current) {
        setIsLessonLoading(true);
        const nonce = Date.now();
        setIframeNonce(nonce);
        iframeRef.current.src = buildIframeSrc(data.template_id, nonce);
      }
        } catch (error) {
          console.error('Error copying lesson:', error);
          alert('Failed to copy lesson. Please try again.');
        } finally {
          setIsCopying(false);
        }
      };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    if (deleteStep === 1) {
      setDeleteStep(2);
      // Fetch fresh deployment info so checkbox is accurate on step 2
      try {
        const res = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateToDelete.id)}/deployment`, withAuthFetchOptions());
        const d = await res.json();
        const url = d?.deployment?.deployment_url || null;
        if (url) setDeleteModalDeploymentUrl(url);
      } catch (_) {}
      return;
    }

    // Second confirmation - proceed with deletion
    setIsDeletingTemplate(true);
    try {
          const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateToDelete.id)}`, withAuthFetchOptions({
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ delete_worker: deleteWorker }),
          }));

      const data = await response.json();

          if (data.success) {
        // Clear chat history for the deleted template BEFORE reloading templates
        console.log(`[App] Deleting lesson ${templateToDelete.id}, clearing chat history...`);
        clearTemplateChatHistory(templateToDelete.id);

        // Reload templates list
            const templatesResponse = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
            const templatesData = await templatesResponse.json();
            if (templatesData.templates && templatesData.templates.length > 0) {
              setAvailableTemplates(templatesData.templates);
            syncTemplateOwnershipFromList(templatesData.templates);
          if (selectedTemplate === templateToDelete.id) {
            const next = templatesData.templates[0].id;
            setSelectedTemplate(next);
            if (iframeRef.current) {
              setIsLessonLoading(true);
              const nonce = Date.now();
              setIframeNonce(nonce);
              iframeRef.current.src = buildIframeSrc(next, nonce);
            }
          }
        } else {
          setAvailableTemplates([]);
          if (selectedTemplate === templateToDelete.id) {
            setSelectedTemplate(null);
          }
        }
        // Close modal, reset, and go to gallery
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        setDeleteStep(1);
        setGalleryViewMode(true);
          } else {
            alert(`Error deleting lesson: ${data.error || data.detail || 'Unknown error'}`);
          }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(`Error deleting lesson: ${error.message}`);
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
    setDeleteStep(1);
    setDeleteWorker(false);
    setDeleteModalDeploymentUrl(null);
  };

  const handleOpenTemplateFromGallery = (templateId) => {
    setGalleryViewMode(false); // enter slide edit view
    setLessonError(null);
    setIsLessonLoading(true); // always set so when iframe loads we clear it (async: can open other apps while one is creating)
    setSelectedTemplate(templateId);
    if (pendingTemplateId && templateId === pendingTemplateId) {
      return;
    }
    if (iframeRef.current) {
      const nonce = Date.now();
      setIframeNonce(nonce);
      iframeRef.current.src = buildIframeSrc(templateId, nonce);
    }
  };

  const MAX_TEMPLATE_IMAGES = 6;
  const readTemplateFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  const handleTemplateImageSelect = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    const valid = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Please select only image files');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(`Image ${file.name} must be less than 10MB`);
        continue;
      }
      valid.push(file);
    }
    const toAdd = valid.slice(0, MAX_TEMPLATE_IMAGES - newTemplateImages.length);
    if (toAdd.length === 0) {
      alert(`You can add up to ${MAX_TEMPLATE_IMAGES} reference images.`);
      if (templateImageInputRef.current) templateImageInputRef.current.value = '';
      return;
    }
    const newFiles = [...newTemplateImages, ...toAdd].slice(0, MAX_TEMPLATE_IMAGES);
    setNewTemplateImages(newFiles);
    try {
      const newPreviews = await Promise.all(toAdd.map(readTemplateFileAsDataURL));
      setNewTemplateImagePreviews((prev) => [...prev, ...newPreviews].slice(0, MAX_TEMPLATE_IMAGES));
    } catch (err) {
      console.error('Error reading template images:', err);
      setNewTemplateImages(newTemplateImages);
    }
    if (templateImageInputRef.current) templateImageInputRef.current.value = '';
  };

  const removeTemplateImage = (index) => {
    if (index !== undefined) {
      setNewTemplateImages((prev) => prev.filter((_, i) => i !== index));
      setNewTemplateImagePreviews((prev) => prev.filter((_, i) => i !== index));
    } else {
      setNewTemplateImages([]);
      setNewTemplateImagePreviews([]);
    }
    if (templateImageInputRef.current) templateImageInputRef.current.value = '';
  };

  const handleTemplateFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (!['csv', 'xlsx', 'xls', 'pdf', 'docx', 'pptx'].includes(fileExt)) {
        alert('Please select a supported file (.csv, .xlsx, .xls, .pdf, .docx, .pptx)');
        return;
      }
      
      // Validate file size (max 50MB for data files)
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }
      
      setNewTemplateFile(file);
      
      // Upload the file immediately
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: formData,
        });
        
        const data = await response.json();
        if (data.success) {
          setNewTemplateFilePath(data.file_path);
          console.log(`Template file uploaded: ${data.file_path}`);
        } else {
          alert(`Upload failed: ${data.error || 'Unknown error'}`);
          setNewTemplateFile(null);
        }
      } catch (error) {
        console.error('Error uploading template file:', error);
        alert('Failed to upload file. Please try again.');
        setNewTemplateFile(null);
      }
    }
  };

  const removeTemplateFile = () => {
    setNewTemplateFile(null);
    setNewTemplateFilePath(null);
    if (templateFileInputRef.current) {
      templateFileInputRef.current.value = '';
    }
  };

  const handleAssessmentFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['.pdf', '.docx', '.pptx', '.csv', '.xlsx', '.xls'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) {
      alert(`Unsupported file type. Please upload: ${allowed.join(', ')}`);
      e.target.value = '';
      return;
    }
    setNewTemplateAssessmentFile(file);
    setAssessmentFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/upload`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.success) {
        setNewTemplateAssessmentFilePath(data.file_path);
      } else {
        alert(`Upload failed: ${data.error || 'Unknown error'}`);
        setNewTemplateAssessmentFile(null);
        if (assessmentFileInputRef.current) assessmentFileInputRef.current.value = '';
      }
    } catch (err) {
      alert('Failed to upload file. Please try again.');
      setNewTemplateAssessmentFile(null);
      if (assessmentFileInputRef.current) assessmentFileInputRef.current.value = '';
    } finally {
      setAssessmentFileUploading(false);
    }
  };

  const removeAssessmentFile = () => {
    setNewTemplateAssessmentFile(null);
    setNewTemplateAssessmentFilePath(null);
    if (assessmentFileInputRef.current) assessmentFileInputRef.current.value = '';
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data URL prefix (data:image/png;base64,)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const deriveLessonName = (description) => {
    const cleaned = (description || '').replace(/\s+/g, ' ').trim();
    if (!cleaned) return 'New Lesson';
    const firstSentence = cleaned.split(/[.!?]/)[0] || cleaned;
    const words = firstSentence.split(' ').filter(Boolean).slice(0, 6);
    return words.join(' ') || 'New Lesson';
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateIsAssessment && !newTemplateDescription.trim()) {
      alert('Please provide a lesson description');
      return;
    }
    if (newTemplateIsAssessment && newTemplateAssessmentSource === 'topic' && !newTemplateAssessmentTopic.trim()) {
      alert('Please enter a topic for the assessment');
      return;
    }
    if (newTemplateIsAssessment && newTemplateAssessmentSource === 'template_ids' && !newTemplateAssessmentTemplateIds.trim()) {
      alert('Please select at least one lesson to base the assessment on');
      return;
    }
    if (newTemplateIsAssessment && newTemplateAssessmentSource === 'uploaded_file' && !newTemplateAssessmentFilePath) {
      alert('Please upload a file to base the assessment on');
      return;
    }
    if (createInFlightRef.current) {
      return; // already creating (e.g. double-click) -- only one create at a time
    }
    createInFlightRef.current = true;
setIsCreatingTemplate(true);

// Save form data before clearing
const previousTemplateId = selectedTemplate;
// ── Save assessment fields before modal clears ──
const savedIsAssessment = newTemplateIsAssessment;
const savedAssessmentSource = newTemplateAssessmentSource;
const savedAssessmentTopic = newTemplateAssessmentTopic;
const savedAssessmentTemplateIds = newTemplateAssessmentTemplateIds
  .split(',').map(s => s.trim()).filter(Boolean);
const savedAssessmentFilePath = newTemplateAssessmentFilePath;
const savedQuestionCount = newTemplateQuestionCount ? parseInt(newTemplateQuestionCount) : null;
const savedName = newTemplateName.trim() ||
  (savedIsAssessment
    ? (savedAssessmentTopic.trim() || 'Assessment')
    : deriveLessonName(newTemplateDescription));
const savedDescription = newTemplateDescription.trim();
const imagesToSend = [...newTemplateImages];
    setCreatingTemplateName(savedName);
    setCreatingTemplateDescription(savedDescription);
    // Save assessment fields before modal clears


    // Optimistically add the template to the list (use unique id so only this card shows "Creating...")
    const tempTemplateId = `creating-${Date.now()}`;
    const tempTemplate = { id: tempTemplateId, name: savedName, is_assessment: savedIsAssessment };
    setAvailableTemplates(prev => [...prev, tempTemplate]);
    setSelectedTemplate(tempTemplateId);
    setPendingTemplateId(tempTemplateId);
    setIsLessonLoading(true);
    setLessonError(null);

    // Close modal immediately for better UX
    setShowAddTemplateModal(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateModel(OPENAI_MODEL_ID); // Reset to default model
    setNewTemplateIsAssessment(false);
    setNewTemplateAssessmentSource('topic');
    setNewTemplateAssessmentTopic('');
    setNewTemplateAssessmentTemplateIds('');
    setNewTemplateQuestionCount('');
    removeAssessmentFile();
    
    // Prepare image data (multiple)
    const imagesPayload = [];
    for (let i = 0; i < imagesToSend.length; i++) {
      const item = imagesToSend[i];
      if (item instanceof Blob || item instanceof File) {
        const b64 = await convertImageToBase64(item);
        imagesPayload.push({ base64: b64, mime_type: item.type || 'image/png' });
      } else if (typeof item === 'string' && item.includes('base64,')) {
        // Already a data URL from backlog preview
        imagesPayload.push({ base64: item.split('base64,')[1], mime_type: 'image/png' });
      } else if (typeof item === 'string') {
        // Plain base64 string
        imagesPayload.push({ base64: item, mime_type: 'image/png' });
      }
      // Skip plain objects like { name: 'ref_image_0' } with no actual image data
    }
    
    // Clear image and file state
    removeTemplateImage();
    removeTemplateFile();
    
    try {
          const requestBody = {
            name: savedName,
            description: savedDescription,
            model: newTemplateModel
          };
      const storedKeys = getStoredApiKeys();
      if (storedKeys.google?.trim()) requestBody.google_key = storedKeys.google.trim();
      if (storedKeys.openai?.trim()) requestBody.openai_key = storedKeys.openai.trim();
      if (storedKeys.deepseek?.trim()) requestBody.deepseek_key = storedKeys.deepseek.trim();

      if (imagesPayload.length === 1) {
        requestBody.image_base64 = imagesPayload[0].base64;
        requestBody.image_mime_type = imagesPayload[0].mime_type;
      } else if (imagesPayload.length > 1) {
        requestBody.images = imagesPayload;
      }
      
      if (newTemplateFilePath) {
        requestBody.uploaded_file_path = newTemplateFilePath;
      }
      
          let data = {};
      if (savedIsAssessment) {
        const assessBody = {
  template_id: tempTemplateId,
  source: savedAssessmentSource,
  topic: savedAssessmentTopic || savedName,
  template_ids: savedAssessmentTemplateIds,
  uploaded_file_path: savedAssessmentFilePath || undefined,
  question_count: savedQuestionCount ? Number(savedQuestionCount) : undefined,
  model: newTemplateModel,
};
        const storedKeys2 = getStoredApiKeys();
        if (storedKeys2.google?.trim()) assessBody.google_key = storedKeys2.google.trim();
        if (storedKeys2.openai?.trim()) assessBody.openai_key = storedKeys2.openai.trim();
        if (storedKeys2.deepseek?.trim()) assessBody.deepseek_key = storedKeys2.deepseek.trim();

        const assessResponse = await fetch(`${API_URL}/lesson/generate-assessment`, withAuthFetchOptions({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assessBody),
        }));

        const assessRawText = await assessResponse.text();
        const parseJsonSafely2 = (text) => {
          if (!text) return {};
          try { return JSON.parse(text); }
          catch {
            const match = text.match(/\{[\s\S]*\}/);
            if (match) { try { return JSON.parse(match[0]); } catch { return {}; } }
            return {};
          }
        };
        data = parseJsonSafely2(assessRawText);
        if (!assessResponse.ok || data.blocked) {
          const messageFromData = data.block_reason || data.error || (Array.isArray(data.detail) ? data.detail.map(e => e.msg).join(", ") : typeof data.detail === "object" && data.detail !== null ? JSON.stringify(data.detail) : data.detail) || data.reason || data.message || '';
          const messageFromText = assessRawText && !/^\s*[\{\[]/.test(assessRawText) ? assessRawText : '';
          const resolvedMessage = messageFromData || messageFromText || 'Assessment generation failed.';
          setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
          setPendingTemplateId(null);
          setSelectedTemplate(previousTemplateId);
          if (iframeRef.current) {
            setIsLessonLoading(true);
            const nonce = Date.now();
            setIframeNonce(nonce);
            iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
          }
          setIsLessonLoading(false);
          setCreatingTemplateName('');
          setCreatingTemplateDescription('');
          openInlineAlert(resolvedMessage);
          setIsCreatingTemplate(false);
          return;
        }
        // Falls through to the polling block below
      // Falls through to the polling block below
      } else {
      // ── Normal lesson path ────────────────────────────────────────────────
      const response = await fetch(`${API_URL}/board/templates/create`, withAuthFetchOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }));
      const rawText = await response.text();
      data = {};
      const parseJsonSafely = (text) => {
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch (error) {
          const match = text.match(/\{[\s\S]*\}/);
          if (match) {
            try {
              return JSON.parse(match[0]);
            } catch (innerError) {
              return {};
            }
          }
          return {};
        }
      };
      data = parseJsonSafely(rawText);
          const messageFromData = data.block_reason || data.error || (Array.isArray(data.detail) ? data.detail.map(e => e.msg).join(", ") : typeof data.detail === "object" && data.detail !== null ? JSON.stringify(data.detail) : data.detail) || data.reason || data.message || '';
      const messageFromText = rawText && !/^\s*[\{\[]/.test(rawText) ? rawText : '';
      const resolvedMessage = messageFromData || messageFromText || 'Request failed. Please try again.';
      
      if (!response.ok) {
        const msg = messageFromData || messageFromText || `HTTP ${response.status}: ${response.statusText}`;
        setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
        setPendingTemplateId(null);
          setSelectedTemplate(previousTemplateId);
          if (iframeRef.current) {
            setIsLessonLoading(true);
            const nonce = Date.now();
            setIframeNonce(nonce);
            iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
          }
        setIsLessonLoading(false);
        setCreatingTemplateName('');
        setCreatingTemplateDescription('');
        openInlineAlert(msg);
        setIsCreatingTemplate(false);
        return;
      }
      
      if (data.blocked) {
        setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
        setPendingTemplateId(null);
        setSelectedTemplate(previousTemplateId);
        if (iframeRef.current) {
          setIsLessonLoading(true);
          const nonce = Date.now();
          setIframeNonce(nonce);
          iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
        }
        setIsLessonLoading(false);
        setCreatingTemplateName('');
        setCreatingTemplateDescription('');
        openInlineAlert(resolvedMessage);
        setIsCreatingTemplate(false);
        return;
      }
      } // close else (normal lesson path)

      // ── Background generation: backend returned 202 with a real template ID ─
      if (data.status === 'creating' && data.template_id) {
        const realId = data.template_id;

        // Swap the optimistic placeholder for the real ID so the gallery card
        // and ChatAdvisor both update immediately.
        setAvailableTemplates(prev =>
          prev.map(t => t.id === tempTemplateId ? { ...t, id: realId } : t)
        );
        setSelectedTemplate(realId);
        selectedTemplateRef.current = realId;
        setPendingTemplateId(realId);
        setGalleryViewMode(false);
        generationStartRef.current = Date.now();
        stageTimesRef.current = {};
        localStorage.setItem('_pending_gen', JSON.stringify({ id: realId, name: savedName }));

        // Poll /create-status every 3 s for up to 30 minutes
        const maxPolls = 600;
        let finalResult = null;
        for (let i = 0; i < maxPolls; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const sr = await fetch(
              `${API_URL}/board/templates/${encodeURIComponent(realId)}/create-status`,
              withAuthFetchOptions({})
            );
            if (!sr.ok) continue;
            const sd = await sr.json();
            if (sd.stage) {
              if (!stageTimesRef.current[sd.stage]) stageTimesRef.current[sd.stage] = Date.now();
              setGenerationStage(sd.stage);
            }
            if (sd.status === 'done') { setIsRegeneratingLesson(false); setGenerationStage(null); setShowProgressModal(false); finalResult = sd.result || { success: true, template_id: realId }; break; }
            if (sd.status === 'error') {
              setIsRegeneratingLesson(false); setGenerationStage(null); setShowProgressModal(false);
              finalResult = { success: false, error: sd.error || 'Generation failed' };
              break;
            }
            if (sd.status === 'regenerating') {
              stageTimesRef.current = {}; generationStartRef.current = Date.now();
              setIsRegeneratingLesson(true); setGenerationStage('stage1_domain_playbook');
              stageTimesRef.current['stage1_domain_playbook'] = Date.now();
            }
          } catch (_) { /* network blip — keep polling */ }
        }
        if (!finalResult) finalResult = { success: false, error: 'Generation timed out. It may still complete — check your gallery.' };
        data = finalResult;
      }

      // ── Handle final result (same path whether sync or async) ───────────────
      if (data.success) {
        const finalId = data.template_id || selectedTemplateRef.current;
        await new Promise(r => setTimeout(r, 300));
        try {
          const tr = await fetch(`${API_URL}/board/templates`, withAuthFetchOptions());
          const td = await tr.json();
          if (td.templates?.length > 0) {
            setAvailableTemplates(td.templates);
            syncTemplateOwnershipFromList(td.templates);
          }
        } catch (_) {}
        localStorage.setItem(`chat_model_${finalId}`, newTemplateModel);
        setPendingTemplateId(null);
        if (selectedTemplateRef.current === finalId || selectedTemplateRef.current === tempTemplateId) {
          setSelectedTemplate(finalId);
          // Reload chat history — backend seeds it during creation but the panel loaded too early (before creation finished)
          setTimeout(() => window.dispatchEvent(new CustomEvent('chatHistoryRefresh', { detail: { template: finalId } })), 800);
          if (window.__pendingBacklogId) {
            fetch(`${API_URL}/my/tasks/${window.__pendingBacklogId}/link-lesson`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentToken}` },
              body: JSON.stringify({ lesson_id: finalId }),
            }).catch(() => {});
            window.__pendingBacklogId = null;
          }
          if (iframeRef.current) {
            setIsLessonLoading(true);
            setLessonError(null);
            const nonce = Date.now(); setIframeNonce(nonce);
            iframeRef.current.src = buildIframeSrc(finalId, nonce);
          }
          // Kick off QA in background — lesson is already visible
          try {
            await fetch(`${API_URL}/board/templates/${encodeURIComponent(finalId)}/evaluate`, withAuthFetchOptions({ method: 'POST' }));
            console.log('[QA] polling started for', finalId);
            setQaRunning(true);
            startQaPolling(finalId);
          } catch (_) {}
        }
        setBuildCompleteModal({ id: finalId, name: savedName || data.template_name || finalId });
        setIsRegeneratingLesson(false);
        if (data.has_validation_warnings) {
          const lines = Array.isArray(data.validation_errors) ? data.validation_errors : [];
          console.warn('[LessonGen] Validation warnings:', lines);
        }
        setPendingTemplateId(null);
      } else {
        const failId = data.template_id || selectedTemplateRef.current || tempTemplateId;
        setAvailableTemplates(prev => prev.filter(t => t.id !== failId && t.id !== tempTemplateId));
        setPendingTemplateId(null);
        setSelectedTemplate(previousTemplateId);
        if (iframeRef.current) {
          setIsLessonLoading(true);
          const nonce = Date.now(); setIframeNonce(nonce);
          iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
        }
        setIsLessonLoading(false);
        openInlineAlert(`Error creating lesson: ${data.error || 'Generation failed.'}`);
      }
    } catch (error) {
      console.error('Error creating template:', error);
      setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
      setPendingTemplateId(null);
      setSelectedTemplate(previousTemplateId);
      if (iframeRef.current) {
        setIsLessonLoading(true);
        const nonce = Date.now(); setIframeNonce(nonce);
        iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
      }
      setIsLessonLoading(false);
      setCreatingTemplateName('');
      setCreatingTemplateDescription('');
      openInlineAlert(`Error creating lesson: ${error.message}`);
    } finally {
      createInFlightRef.current = false;
      setIsCreatingTemplate(false);
      setIsRegeneratingLesson(false);
      setCreatingTemplateName('');
      setCreatingTemplateDescription('');
      // buildCompleteModal stays open — user dismisses it
    }
  };

  const handleStartupSignIn = () => loginWithRedirect({
  authorizationParams: { prompt: 'login' }
});

  if (authChecking) {
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Checking session...</div>
          <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>Please wait</div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && !roleReady) {
    // If currentRole is explicitly null (token fetched but no role assigned), show the contact-admin screen
    if (currentRole === null) {
      return (
        <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
          <div style={{ width: 'min(440px, calc(100vw - 24px))', padding: '20px', borderRadius: '14px', background: '#fff', color: '#0f172a', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
            <h2 style={{ margin: '0 0 8px 0' }}>No role assigned</h2>
            <p style={{ color: '#475569', margin: '0 0 14px 0' }}>
              Your account hasn't been assigned a role yet. Please contact your administrator to get access.
              Once assigned, click <strong>Refresh</strong> to continue.
            </p>
            <button
              onClick={async () => {
                // Force a fresh token (bypasses cache) so new role is picked up without clearing cache
                try {
                  const token = await getAccessTokenSilently({
                    cacheMode: 'off',
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE }
                  });
                  setCurrentToken(token);
                  const tp = JSON.parse(atob(token.split('.')[1]));
                  const roles = (tp['https://lessongen/roles'] || []).map(r => r.trim());
                  if (roles.length > 0) {
                    const primaryRole = roles.includes('manager') ? 'manager'
                      : roles.includes('final_approver') ? 'final_approver'
                      : roles.includes('reviewer') ? 'reviewer'
                      : roles.includes('distributor') ? 'distributor'
                      : roles.includes('creator') ? 'creator'
                      : roles.includes('learner') ? 'learner'
                      : roles[0];
                    setCurrentRole(primaryRole);
                    setRoleReady(true);
                  }
                } catch (e) {
                  console.error('Refresh failed:', e);
                }
              }}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', background: '#1d4ed8', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', marginBottom: '8px' }}
            >
              Refresh
            </button>
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
            >
              Sign out
            </button>
          </div>
        </div>
      );
    }

    // Still fetching token — show spinner with a timeout fallback
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>Loading your profile...</div>
          <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>Please wait</div>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ width: 'min(440px, calc(100vw - 24px))', padding: '20px', borderRadius: '14px', background: '#ffffff', color: '#0f172a', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
          <h2 style={{ margin: '0 0 8px 0' }}>Sign in required</h2>
          <p style={{ margin: '0 0 14px 0', color: '#475569' }}>
            Sign in first, then the gallery and lessons will load.
          </p>
          <button
            onClick={handleStartupSignIn}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
          >
            Sign in with Auth0
          </button>
        </div>
      </div>
    );
  }

  if (isAuthenticated && currentRole === 'learner') {
    return <LearnerView token={currentToken} />;
  }

  

  if (isAuthenticated && !authChecking && roleReady && !currentRole) {
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#e2e8f0' }}>
        <div style={{ width: 'min(440px, calc(100vw - 24px))', padding: '20px', borderRadius: '14px', background: '#fff', color: '#0f172a', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
          <h2 style={{ margin: '0 0 8px 0' }}>No role assigned</h2>
          <p style={{ color: '#475569', margin: '0 0 14px 0' }}>Your account has no role assigned. Please contact your manager.</p>
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin} })}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (isAuthenticated && window.location.pathname === '/monitor-cost') {
    return <CostMonitor apiBase={API_URL} token={currentToken} />;
  }

  return (
    <div className="App">
      {showAdmin && (
        <div className="admin-modal-backdrop" onClick={() => setShowAdmin(false)}>
          <div className="admin-modal-panel" onClick={e => e.stopPropagation()}>
            <AdminPage token={currentToken} userRole={currentRole} onExit={() => setShowAdmin(false)} />
          </div>
        </div>
      )}
      {showAdmin && (
        <div className="admin-modal-backdrop" onClick={() => setShowAdmin(false)}>
          <div className="admin-modal-panel" onClick={e => e.stopPropagation()}>
            <AdminPage token={currentToken} userRole={currentRole} onExit={() => setShowAdmin(false)} />
          </div>
        </div>
      )}
          <div className={`layout-shell ${chatDocked && !galleryViewMode ? 'chat-docked' : ''} ${chatDocked && !galleryViewMode && chatCollapsed ? 'chat-collapsed' : ''}`}>
      <div className={`left-rail ${galleryViewMode ? 'gallery-mode' : ''}`}>
            <aside className="side-rail">
          <div className="brand-mark mini">
            <img
              src={`${process.env.PUBLIC_URL}/logo/zero2AI_logomain.svg`}
              alt="Zero2AI Logo"
              className="brand-logo mini"
            />
          </div>
          <div className="rail-actions">
            {currentRole === 'manager' && (
            <button className="rail-btn icon rail-new" onClick={() => setShowAddTemplateModal(true)} title="New Lesson">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 5v14M5 12h14"></path>
              </svg>
              <span className="sr-only">New Lesson</span>
            </button>
            )}
            <button 
              className={`rail-btn icon rail-gallery ${galleryNudge ? 'nudge' : ''}`} 
              onClick={() => setGalleryViewMode(true)} 
              title="Gallery"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1"></rect>
                <rect x="14" y="3" width="7" height="7" rx="1"></rect>
                <rect x="3" y="14" width="7" height="7" rx="1"></rect>
                <rect x="14" y="14" width="7" height="7" rx="1"></rect>
              </svg>
              <span className="sr-only">Gallery</span>
              {galleryTooltip && <span className="rail-tooltip">Go to Gallery</span>}
            </button>
            {['manager', 'creator'].includes(currentRole) && <button 
              className={`rail-btn icon rail-chat ${chatNudge ? 'nudge' : ''}`}
                  onClick={() => {
                    setChatDocked(true);
                    setChatCollapsed(false);
                    setChatMinimized(true);
                  }} 
                  title="Lesson Copilot"
                >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 11.5a8.38 8.38 0 0 1-1.9 5.4 8.5 8.5 0 0 1-6.6 3.1 8.38 8.38 0 0 1-5.4-1.9L3 20l1.9-4.1a8.38 8.38 0 0 1-1.9-5.4 8.5 8.5 0 0 1 3.1-6.6A8.38 8.38 0 0 1 11.5 2H12a8.5 8.5 0 0 1 8 9.5Z"></path>
              </svg>
              <span className="sr-only">Open Lesson Copilot</span>
              {chatTooltip && <span className="rail-tooltip">Lesson Copilot</span>}
            </button>}
            <button
              className="rail-btn icon rail-keys"
              onClick={() => setShowKeysModal(true)}
              title="API Keys (Google, OpenAI, Anthropic, DeepSeek)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="7" cy="12" r="3"></circle>
                <path d="M10 12h11"></path>
                <path d="M18 12v3"></path>
                <path d="M15 12v2"></path>
              </svg>
             
              <span className="sr-only">API Keys</span>
              <span className="rail-tooltip auto-hide">API Keys</span>
            </button>
            {['manager', 'organizer'].includes(currentRole) && (
              <button
                className="rail-btn icon rail-admin"
                onClick={() => setShowAdmin(true)}
                title="Admin Panel"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
                <span className="sr-only">Admin Panel</span>
                <span className="rail-tooltip auto-hide">Admin Panel</span>
              </button>
            )}
            {currentRole === 'manager' && (
              <button
                className="rail-btn icon rail-cost"
                onClick={() => window.open('/monitor-cost', '_blank')}
                title="Cost Monitor"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                <span className="sr-only">Cost Monitor</span>
                <span className="rail-tooltip auto-hide">Cost Monitor</span>
              </button>
            )}
            <button
              className="rail-btn icon"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin} })}
              title={`Sign out (${user?.email})`}
              style={{ marginTop: 'auto' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span className="sr-only">Sign out</span>
              <span className="rail-tooltip auto-hide">Sign out ({user?.email})</span>
            </button>
          </div>
        </aside>
            {chatDocked && !galleryViewMode && (
              <aside className={`chat-sidebar ${chatCollapsed ? 'collapsed' : ''}`}>
                <div className="chat-sidebar-header">
                  {!chatCollapsed && <span className="chat-sidebar-title">Lesson Copilot</span>}
                  <div className="chat-sidebar-actions">
                <button
                  className="icon-btn small"
                  onClick={() => setChatCollapsed(!chatCollapsed)}
                  title={chatCollapsed ? 'Expand chat' : 'Collapse chat'}
                >
                  {chatCollapsed ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  )}
                  <span className="sr-only">{chatCollapsed ? 'Expand chat' : 'Collapse chat'}</span>
                </button>
                <button
                  className="icon-btn small"
                  onClick={() => {
                    setChatDocked(false);
                    setChatMinimized(false);
                  }}
                  title="Pop out chat"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 3h3v3"></path>
                    <path d="M21 3l-7 7"></path>
                    <rect x="3" y="7" width="14" height="14" rx="2" ry="2"></rect>
                  </svg>
                  <span className="sr-only">Pop out chat</span>
                </button>
              </div>
            </div>
            {!chatCollapsed && (
              <div className="chat-sidebar-body">
                {selectedTemplate ? (
                      <ChatAdvisor
                        key={`chat-${selectedTemplate}`}
                        selectedTemplate={selectedTemplate}
                        isOwner={templateOwnership[selectedTemplate]?.can_edit ?? true}
                        isCreatingTemplate={isCreatingTemplate}
                        creatingTemplateDescription={creatingTemplateDescription}
                        pendingTemplateId={pendingTemplateId}
                        apiKeysConfig={apiKeysConfig}
                        defaultModelForTemplate={availableTemplates.find(t => t.id === selectedTemplate)?.default_model}
                      isEditMode={isEditMode}
                      setIsEditMode={setIsEditMode}
                      />
                ) : (
                  <div style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>It's empty</p>
                    <p>Select or create a lesson to chat with the copilot.</p>
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>
              <div className="main-pane">
          <div className="top-actions compact">
            <img
              src={`${process.env.PUBLIC_URL}/logo/Arbiteria_withTL.png`}
              alt="Arbiteria logo"
              className="top-right-logo-abs"
            />
            <div className="top-brand-center">
              <div className="brand-mark mini center">
                <div className="brand-circles mini">
                  <span></span><span></span><span></span>
                </div>
                <div className="brand-type">
                  <div className="brand-kicker">
                    <span className="brand-lesson">Lesson</span>
                    <span className="brand-gen">Gen</span>
                  </div>
                  <div className="brand-tagline">Interactive mini-lessons</div>
                </div>
              </div>
            </div>
            <div className="top-actions-mid">
</div>

            <div className="top-actions-right">
              <span className="save-status">{saveStatus}</span>
              
              <button className="icon-btn" onClick={handleSave} title="Save inputs">
                <span aria-hidden>💾</span>
                <span className="sr-only">Save</span>
              </button>
              <button className="icon-btn" onClick={handleClear} title="Clear saved state">
                <span aria-hidden>🧹</span>
                <span className="sr-only">Clear</span>
              </button>
            </div>
          </div>
      <div className={`main-layout ${isFullscreen ? 'fullscreen-mode' : ''} ${galleryViewMode ? 'gallery-view-mode' : ''}`}>
        <div className={`lesson-column canvas ${isFullscreen ? 'fullscreen' : ''} ${galleryViewMode ? 'gallery-view' : ''}`}>
          <div className="inline-gallery" style={{ display: galleryViewMode ? 'block' : 'none' }} aria-hidden={!galleryViewMode}>
              {availableTemplates.length === 0 ? (
                <div className="gallery-empty-state" style={{ textAlign: 'center', padding: '3rem', color: '#facc15' }}>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>It's empty here</p>
                  <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No lessons yet.</p>
                  <p style={{ marginBottom: '1rem' }}>Contact your administrator to start your experience.</p>
                  {currentRole === 'manager' && (
                  <button
                    className="modal-button primary"
                    onClick={() => setShowAddTemplateModal(true)}
                  >
                    Create lesson
                  </button>
                  )}
                </div>
              ) : (
              <div className="gallery-grid inline">
                {availableTemplates.map((template) => {
                  const isPendingPreview = pendingTemplateId && template.id === pendingTemplateId;
                  return (
                  <div
                    key={template.id}
                    className={`gallery-item inline${isPendingPreview ? ' gallery-item-generating' : ''}`}
                    onClick={() => !isPendingPreview && handleOpenTemplateFromGallery(template.id)}
                    title={isPendingPreview ? '' : `Open ${template.name}`}
                    style={isPendingPreview ? { cursor: 'default' } : {}}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      const centerX = rect.width / 2;
                      const centerY = rect.height / 2;
                      const rotateX = ((y - centerY) / centerY) * -5;
                      const rotateY = ((x - centerX) / centerX) * 5;
                      e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
                      e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
                      e.currentTarget.style.setProperty('--rotate-x', `${rotateX}deg`);
                      e.currentTarget.style.setProperty('--rotate-y', `${rotateY}deg`);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.setProperty('--mouse-x', `50%`);
                      e.currentTarget.style.setProperty('--mouse-y', `50%`);
                      e.currentTarget.style.setProperty('--rotate-x', `0deg`);
                      e.currentTarget.style.setProperty('--rotate-y', `0deg`);
                    }}
                  >
                    {isPendingPreview && (
                      <button
                        className="gallery-monitor-btn-top"
                        onClick={e => { e.stopPropagation(); setShowProgressModal(true); }}
                      >
                        Activity Tracking
                      </button>
                    )}
                    <div className="gallery-card">
                      <div className="gallery-face front">
                        <div className="gallery-hover-anchor">
                          {!isPendingPreview && (
                          <div className="gallery-hover-bubble">
                            <p className="bubble-title">{template.name}</p>
                            <p className="bubble-text">
                              {template.description && template.description.trim().length > 0
                                ? template.description
                                : 'Interactive lesson with multimedia screens, checks, and a scored assessment.'}
                            </p>
                            <p className="bubble-text subtle">
                              {template.estimated_minutes
                                ? `${template.estimated_minutes} min · ARCS hooks + assessment`
                                : 'ARCS hooks + assessment (80% to certify)'}
                            </p>
                            <span className="bubble-cta">Click to open & edit</span>
                          </div>
                          )}
                        </div>
                        <div className="gallery-item-preview">
                          {isPendingPreview ? (
                            <div className="gallery-preview-pending">
                              <div className="gallery-loading-spinner"></div>
                            </div>
                          ) : (
                            <div className="gallery-preview-static">
                              {/* Fallback text always rendered; image layers on top and hides on error */}
                              <div className="gallery-static-badge" style={template.is_assessment ? { background: '#6366f1', color: '#fff' } : {}}>
                                {template.is_assessment ? '📝 Assessment' : 'Lesson'}
                              </div>
                              <div className="gallery-static-title">{template.name}</div>
                              <div className="gallery-static-subtitle">
                                {template.estimated_minutes
                                  ? `${template.estimated_minutes} min`
                                  : 'Open to view and edit'}
                              </div>
                              {template.thumbnail_url && (
                                <img
                                  src={`${API_URL}${template.thumbnail_url}`}
                                  alt=""
                                  className="gallery-static-image"
                                  loading="lazy"
                                  style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',borderRadius:10}}
                                  onError={e => { e.target.style.display = 'none'; }}
                                />
                              )}
                              {/* Assessment corner badge — sits above thumbnail via z-index */}
                              {template.is_assessment && (
                                <span style={{
                                  position: 'absolute', top: '8px', right: '8px', zIndex: 4,
                                  padding: '3px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: 700,
                                  background: '#6366f1', color: '#fff',
                                  boxShadow: '0 2px 6px rgba(99,102,241,0.5)',
                                  letterSpacing: '0.02em', lineHeight: 1.4,
                                }}>Assessment</span>
                              )}
                            </div>
                          )}
                          {/* Lesson name bar overlaid at bottom of preview for all non-generating cards */}
                          {!isPendingPreview && (
                            <div className="gallery-preview-name-bar">
                              <span>{template.name}</span>
                            </div>
                          )}
                        </div>
                        <div className="gallery-item-info">
                          <h3 className="gallery-item-name">{template.name}</h3>
                          {template.is_assessment && (
                            <span style={{
                              display: 'inline-block', marginTop: '2px',
                              padding: '1px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                              background: 'rgba(99,102,241,0.12)', color: '#6366f1',
                            }}>📝 Assessment</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );})}
              </div>
              )}
            </div>
          <div className="lesson-frame" ref={lessonFrameRef} style={{ display: galleryViewMode ? 'none' : 'block' }} aria-hidden={galleryViewMode}>
            {!selectedTemplate ? (
              <div className="gallery-empty-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '320px', textAlign: 'center', padding: '2rem', color: '#facc15' }}>
                <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>It's empty here</p>
                <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No lesson selected.</p>
                <p style={{ marginBottom: '1rem' }}>Switch to gallery view to open a lesson, or create a new one.</p>
                {currentRole === 'manager' && (
                <button
                  className="modal-button primary"
                  onClick={() => { setGalleryViewMode(true); setShowAddTemplateModal(true); }}
                >
                  Create lesson
                </button>
                )}
              </div>
            ) : (
              <>
            {/* Only show full-screen overlay when viewing the lesson that's being created; when user switched to another lesson, let that iframe load without blocking */}
            {isLessonLoading && !lessonError && (!isCreatingTemplate || selectedTemplate === pendingTemplateId) && (
              <div className="lesson-loading-overlay">
                {(isCreatingTemplate || isRegeneratingLesson) && selectedTemplate === pendingTemplateId ? (
                  (() => {
                    const STAGE_STEPS = {
                      stage1_domain_playbook: 1,
                      stage2_content_map: 2,
                      stage3_interaction_assessment: 3,
                      stage4_media_generation: 4,
                      stage5_html_build: 5,
                      normalizing: 5,
                      validating: 5,
                    };
                    const currentStep = STAGE_STEPS[generationStage] || 1;
                    const steps = [
                      { step: 1, label: 'Analysing domain & goals' },
                      { step: 2, label: 'Building content map' },
                      { step: 3, label: 'Interactions & assessment' },
                      { step: 4, label: 'Generating images & videos' },
                      { step: 5, label: 'Writing lesson HTML' },
                    ];
                    return (
                      <div className="gen-progress-container">
                        <div className="gen-progress-title">
                          {isRegeneratingLesson
                            ? 'Refining lesson (attempt 2)'
                            : `Creating "${creatingTemplateName || 'lesson'}"…`}
                        </div>
                        <div className="gen-progress-steps">
                          {steps.map(({ step, label }) => {
                            const state = step < currentStep ? 'done' : step === currentStep ? 'active' : 'pending';
                            return (
                              <div key={step} className={`gen-step gen-step-${state}`}>
                                <div className="gen-step-indicator">
                                  {state === 'done' ? '✓' : step}
                                </div>
                                <div className="gen-step-label-wrap">
                                  <span className="gen-step-label">{label}</span>
                                  {state === 'active' && step === 4 && (
                                    <span className="gen-step-note">Images &amp; videos can take 5–15 min</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {isRegeneratingLesson && (
                          <div className="gen-regen-note">Fixing validation errors automatically…</div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <>
                    <div className="lesson-loading-spinner">
                      <div className="spinner-ring"></div>
                      <div className="spinner-ring"></div>
                      <div className="spinner-ring"></div>
                      <div className="spinner-ring"></div>
                    </div>
                    <div className="lesson-loading-text">Loading lesson...</div>
                  </>
                )}
              </div>
            )}
                {lessonError && (
                  <div className="lesson-error-overlay">
                    <div className="lesson-error-icon">!</div>
                    <div className="lesson-error-title">Error Loading Lesson</div>
                    <div className="lesson-error-message">{lessonError}</div>
                  <button
                  className="lesson-error-retry"
                  onClick={() => {
                    setLessonError(null);
                    setIsLessonLoading(true);
                    if (iframeRef.current) {
                      const currentSrc = iframeRef.current.src;
                      iframeRef.current.src = '';
                      setTimeout(() => {
                        if (iframeRef.current) {
                          iframeRef.current.src = currentSrc;
                        }
                      }, 100);
                    }
                  }}
                >
                  Retry
                </button>
              </div>
            )}
            <div className="floating-lesson-actions">
              {!galleryViewMode && (
                <>
                {availableTemplates.find(t => t.id === selectedTemplate)?.is_assessment && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                    background: '#6366f1', color: '#fff', letterSpacing: '0.03em',
                    boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
                  }}>📝 Assessment</span>
                )}
{(templateOwnership[selectedTemplate]?.can_edit ?? true) && (['manager', 'creator'].includes(currentLessonRole) || currentRole === 'manager') && (
<button
  className={`icon-btn round action-edit ${isEditMode ? 'active' : ''}`}
  onClick={() => setIsEditMode(!isEditMode)}
  title={isEditMode ? 'Exit edit mode' : 'Edit lesson content'}
  disabled={!selectedTemplate || !(templateOwnership[selectedTemplate]?.can_edit ?? true)}
>
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
  <span className="sr-only">{isEditMode ? 'Exit edit mode' : 'Edit content'}</span>
</button>
)}
{(currentLessonRole === 'distributor' || currentLessonRole === 'manager' || currentRole === 'manager') && (
  <button
    className="icon-btn round action-deploy"
    onClick={handleDeployClick}
    disabled={isDeploying}
    title={existingDeployment ? "Redeploy to Cloudflare Pages (will create new link if file changed)" : "Deploy to Cloudflare Pages"}
  >
    {isDeploying ? (
      <div className="deploy-spinner"></div>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="17 8 12 3 7 8"></polyline>
        <line x1="12" y1="3" x2="12" y2="15"></line>
      </svg>
    )}
    <span className="sr-only">{existingDeployment ? 'Redeploy' : 'Deploy'}</span>
  </button>
)}
                  {['manager', 'creator'].includes(currentLessonRole) && <button className="icon-btn round action-save" onClick={handleSave} title="Save inputs">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    <span className="sr-only">Save</span>
                  </button>}
                  
                  {['manager', 'creator'].includes(currentLessonRole) && <button className="icon-btn round action-clear" onClick={handleClear} title="Clear saved state">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="1 4 1 10 7 10"></polyline>
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                    </svg>
                    <span className="sr-only">Clear</span>
                  </button>}
                  {['manager', 'creator', 'final_approver'].includes(currentLessonRole) && <button className="icon-btn round action-qa" onClick={handleOpenQaFeedback} style={{backgroundColor: '#8b5cf6', color: 'white', border: '1px solid #7c3aed', animation: qaRunning ? 'qa-btn-pulse 1.2s ease-in-out infinite' : 'none'}} title={qaRunning ? 'QA evaluation running…' : 'QA Report'}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    <span className="sr-only">QA Report</span>
                  </button>}
                </>
              )}
              <button 
                className="icon-btn round action-fullscreen"
                onClick={handleFullscreen}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 3 3 3 3 9"></polyline>
                    <line x1="3" y1="3" x2="9" y2="9"></line>
                    <polyline points="15 21 21 21 21 15"></polyline>
                    <line x1="21" y1="21" x2="15" y2="15"></line>
                    <polyline points="21 9 21 3 15 3"></polyline>
                    <line x1="21" y1="3" x2="15" y2="9"></line>
                    <polyline points="3 15 3 21 9 21"></polyline>
                    <line x1="3" y1="21" x2="9" y2="15"></line>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 3 3 3 3 9"></polyline>
                    <line x1="3" y1="3" x2="9" y2="9"></line>
                    <polyline points="15 21 21 21 21 15"></polyline>
                    <line x1="21" y1="21" x2="15" y2="15"></line>
                  </svg>
                )}
                <span className="sr-only">{isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}</span>
              </button>
              {['manager', 'creator'].includes(currentLessonRole) && <button
                className="icon-btn round action-code"
                onClick={() => setShowCodePreview(true)}
                title="View code"
                disabled={!selectedTemplate}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="16 18 22 12 16 6"></polyline>
                  <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
                <span className="sr-only">View code</span>
              </button>}
              {['manager', 'creator'].includes(currentLessonRole) && <button
                className="icon-btn round action-copy"
                onClick={() => handleCopyClick(selectedTemplate)}
                title={`Copy ${availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}`}
                disabled={!selectedTemplate}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                    <span className="sr-only">Copy lesson</span>
                      </button>}
              {['manager', 'creator'].includes(currentLessonRole) && <button
                className="icon-btn round danger action-delete"
                onClick={() => handleDeleteClick(selectedTemplate)}
                title={`Delete ${availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}`}
                disabled={!selectedTemplate || !(templateOwnership[selectedTemplate]?.is_owner ?? true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                  <path d="M10 11v6"></path>
                  <path d="M14 11v6"></path>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                </svg>
                <span className="sr-only">Delete lesson</span>
              </button>}
              {['manager', 'creator'].includes(currentLessonRole) && <button
                className="icon-btn round action-keys"
                onClick={() => setShowKeysModal(true)}
                title="API Keys (Google, OpenAI, Anthropic, DeepSeek)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="7" cy="12" r="3"></circle>
                  <path d="M10 12h11"></path>
                  <path d="M18 12v3"></path>
                  <path d="M15 12v2"></path>
                </svg>
                <span className="sr-only">API Keys</span>
              </button>}
            </div>
                <div className={`lesson-viewport ${isFullscreen ? 'fullscreen' : ''}`}>
                  {pendingTemplateId && selectedTemplate === pendingTemplateId ? (
                    <div className="lesson-iframe pending" aria-label="Lesson loading"></div>
                  ) : (
                  // REPLACE WITH:
  <>
    <div style={{
      display: 'flex',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    }}>
      {/* Lesson iframe */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <iframe
          ref={iframeRef}
          title="AI Lesson"
          src={buildIframeSrc(selectedTemplate, iframeNonce)}
          className="lesson-iframe"
          key={`${selectedTemplate}-${iframeNonce}`}
        />
        <LessonIframe
          iframeRef={iframeRef}
          lessonFrameRef={lessonFrameRef}
          setIsLessonLoading={setIsLessonLoading}
          selectedTemplate={selectedTemplate}
        />
      </div>

      {['reviewer', 'final_approver', 'creator', 'manager'].includes(currentLessonRole) && selectedTemplate && (
        <ReviewSidebar
          templateId={selectedTemplate}
          currentPageId={currentPageId}
          token={currentToken}
          role={currentLessonRole}
        />
      )}
      {currentLessonRole === 'distributor' && selectedTemplate && (
        <DistributorTeamPanel
          templateId={selectedTemplate}
          token={currentToken}
        />
      )}
    </div>

    
  </>
)}
            </div>
              </>
            )}
          </div>
        </div>
      </div>
      
          {/* Deploy Confirm Modal */}
          {showProgressModal && pendingTemplateId && (() => {
            const GEN_STEPS = [
              { key: 'stage1_domain_playbook', step: 1, label: 'Analysing domain & goals' },
              { key: 'stage2_content_map', step: 2, label: 'Building content map' },
              { key: 'stage3_interaction_assessment', step: 3, label: 'Interactions & assessment' },
              { key: 'stage4_media_generation', step: 4, label: 'Generating images & videos', note: 'This step can take 5–15 min depending on queue' },
              { key: 'stage5_html_build', step: 5, label: 'Writing lesson HTML' },
            ];
            const STAGE_ORDER = ['stage1_domain_playbook','stage2_content_map','stage3_interaction_assessment','stage4_media_generation','stage5_html_build','normalizing','validating'];
            const currentIdx = STAGE_ORDER.indexOf(generationStage);
            const fmtMs = ms => { const s = Math.round(ms / 1000); return s >= 60 ? `${Math.floor(s/60)}m ${s%60}s` : `${s}s`; };
            const now = Date.now(); void progressModalTick;
            const displayName = creatingTemplateName || availableTemplates.find(t => t.id === pendingTemplateId)?.name || 'lesson';
            return (
              <div className="gen-modal-overlay" onClick={() => setShowProgressModal(false)}>
                <div className="gen-modal-panel" onClick={e => e.stopPropagation()}>
                  <div className="gen-modal-header">
                    <span className="gen-modal-title">
                      {isRegeneratingLesson ? 'Refining lesson (attempt 2)…' : `Creating "${displayName}"…`}
                    </span>
                    <button className="gen-modal-close" onClick={() => setShowProgressModal(false)}>×</button>
                  </div>
                  <div className="gen-modal-steps">
                    {GEN_STEPS.map(({ key, step, label, note }) => {
                      const sIdx = STAGE_ORDER.indexOf(key);
                      const state = currentIdx < 0 ? (step === 1 ? 'active' : 'pending') : sIdx < currentIdx ? 'done' : sIdx === currentIdx ? 'active' : 'pending';
                      const startedAt = stageTimesRef.current[key];
                      const nextKey = STAGE_ORDER[sIdx + 1];
                      const endedAt = nextKey ? stageTimesRef.current[nextKey] : null;
                      const elapsed = state === 'done' && startedAt && endedAt ? endedAt - startedAt : state === 'active' && startedAt ? now - startedAt : null;
                      return (
                        <div key={key} className={`gen-modal-step gen-modal-step-${state}`}>
                          <div className="gen-modal-indicator">
                            {state === 'done' ? '✓' : state === 'active' ? <span className="gen-modal-spinner"/> : step}
                          </div>
                          <div className="gen-modal-step-body">
                            <div className="gen-modal-step-label">{label}</div>
                            {state === 'active' && note && <div className="gen-modal-step-note">{note}</div>}
                            {elapsed !== null && (
                              <div className="gen-modal-step-time">
                                {state === 'active' ? `${fmtMs(elapsed)} elapsed` : `Done in ${fmtMs(elapsed)}`}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {isRegeneratingLesson && (
                    <div className="gen-modal-regen-note">Fixing validation errors — restarting from Step 1…</div>
                  )}
                  {generationStartRef.current && (
                    <div className="gen-modal-total">
                      Total elapsed: {fmtMs(now - generationStartRef.current)}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {buildCompleteModal && (
            <div className="modal-overlay" onClick={() => setBuildCompleteModal(null)}>
              <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', textAlign: 'center' }}>
                <div className="modal-header" style={{ justifyContent: 'center', borderBottom: 'none', paddingBottom: 0 }}>
                  <span style={{ fontSize: '2rem' }}>🎉</span>
                  <button className="modal-close" style={{ position: 'absolute', right: '1rem', top: '1rem' }} onClick={() => setBuildCompleteModal(null)}>×</button>
                </div>
                <div className="modal-body" style={{ padding: '0.5rem 1.5rem 1rem' }}>
                  <h2 style={{ margin: '0.5rem 0 0.75rem', fontSize: '1.15rem' }}>Application build completed,<br />you may open now...</h2>
                  <p style={{ color: 'var(--muted)', fontSize: '0.875rem', margin: 0 }}>
                    {buildCompleteModal.name}
                  </p>
                </div>
                <div className="modal-footer" style={{ justifyContent: 'center', gap: '0.75rem' }}>
                  <button className="modal-button cancel" onClick={() => setBuildCompleteModal(null)}>Later</button>
                  <button className="modal-button primary" onClick={() => {
                    const { id } = buildCompleteModal;
                    setBuildCompleteModal(null);
                    setSelectedTemplate(id);
                    selectedTemplateRef.current = id;
                    setGalleryViewMode(false);
                    if (iframeRef.current) {
                      setIsLessonLoading(true);
                      setLessonError(null);
                      const nonce = Date.now(); setIframeNonce(nonce);
                      iframeRef.current.src = buildIframeSrc(id, nonce);
                    }
                  }}>Open Now</button>
                </div>
              </div>
            </div>
          )}

          {showDeployConfirmModal && (
            <div className="modal-overlay" onClick={() => setShowDeployConfirmModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Deploy Lesson</h2>
                  <button className="modal-close" onClick={() => setShowDeployConfirmModal(false)}>
                    ×
                  </button>
                </div>
            <div className="modal-body">
              <p style={{ marginBottom: '12px', color: 'var(--text-primary)' }}>
                Deploy <strong>{availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}</strong> to Cloudflare Pages?
              </p>
              {existingDeployment && (
                <div className="deployment-meta">
                  {existingDeployment.deployed_at && (
                    <div className="deployment-meta-row">
                      <span>Last deployed:</span>
                      <span>{formatDeployTime(existingDeployment.deployed_at)}</span>
                    </div>
                  )}
                  {existingDeployment.deployment_url && (
                    <a href={existingDeployment.deployment_url} target="_blank" rel="noreferrer">
                      {existingDeployment.deployment_url}
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={() => setShowDeployConfirmModal(false)}>
                Cancel
              </button>
              <button className="modal-button primary" onClick={handleDeployConfirm}>
                Deploy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Status Modal */}
      {showDeployModal && (
        <div className="modal-overlay" onClick={() => { if (!isDeploying) setShowDeployModal(false); }}>
          <div className="modal-content deploy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cloudflare Deployment</h2>
              <button
                className="modal-close"
                onClick={() => { if (!isDeploying) setShowDeployModal(false); }}
                disabled={isDeploying}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
                  {isDeploying ? (
                    <div className="deployment-progress">
                      <div className="deploy-spinner large"></div>
                      <h3>Deploying...</h3>
                      <p>Uploading your lesson to Cloudflare Pages.</p>
                    </div>
                  ) : deploymentStatus === 'success' && deploymentUrl ? (
                    <div className="deployment-success">
                  <div className="status-icon">✓</div>
                      <h3>Deployment successful</h3>
                      <p>Your lesson is live.</p>
                      <a href={deploymentUrl} target="_blank" rel="noreferrer">
                        Open deployment
                      </a>
                  {existingDeployment?.production_url && existingDeployment.production_url !== deploymentUrl && (
                    <a href={existingDeployment.production_url} target="_blank" rel="noreferrer">
                      Open production URL
                    </a>
                  )}
                </div>
              ) : deploymentStatus === 'error' ? (
                <div className="deployment-error">
                  <div className="status-icon">!</div>
                  <h3>Deployment failed</h3>
                  <p>Please check your Cloudflare credentials and project settings.</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
<div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '360px' }}>
  {toasts.map(toast => (
    <div key={toast.id} style={{
      background: toast.type === 'success' ? '#059669' : '#3b82f6',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      fontSize: '14px',
      lineHeight: '1.4',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      animation: 'fadeInUp 0.3s ease',
    }}>
      <span style={{ flex: 1 }}>{toast.message}</span>
      <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
        style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0, opacity: 0.8 }}>×</button>
    </div>
  ))}
</div>


      {showInlineAlert && (
        <div className="modal-overlay" onClick={() => setShowInlineAlert(false)}>
          <div className="modal-content inline-alert" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Notice</h2>
              <button className="modal-close" onClick={() => setShowInlineAlert(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                {inlineAlertMessage}
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-button primary" onClick={() => setShowInlineAlert(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Modal */}
      {showKeysModal && (
        <div className="modal-overlay" onClick={() => setShowKeysModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>API Keys</h2>
              <button className="modal-close" onClick={() => setShowKeysModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Add your keys to use your own API quotas. They stay on this device.
              </p>
              <div className="form-group">
                <label htmlFor="api-key-google">Google (Gemini)</label>
                <input
                  id="api-key-google"
                  type="password"
                  value={apiKeyInputs.google}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, google: e.target.value }))}
                  placeholder="AIza..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-openai">OpenAI (GPT)</label>
                <input
                  id="api-key-openai"
                  type="password"
                  value={apiKeyInputs.openai}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, openai: e.target.value }))}
                  placeholder="sk-..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-anthropic">Anthropic (Claude)</label>
                <input
                  id="api-key-anthropic"
                  type="password"
                  value={apiKeyInputs.anthropic}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, anthropic: e.target.value }))}
                  placeholder="sk-ant-..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-deepseek">DeepSeek (V4)</label>
                <input
                  id="api-key-deepseek"
                  type="password"
                  value={apiKeyInputs.deepseek}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, deepseek: e.target.value }))}
                  placeholder="sk-..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={() => setShowKeysModal(false)}>Cancel</button>
              <button className="modal-button primary" onClick={saveApiKeys}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Template Modal */}
      {showAddTemplateModal && (
            <div className="modal-overlay" onClick={() => {
              if (!isCreatingTemplate) {
                setShowAddTemplateModal(false);
                setNewTemplateModel(OPENAI_MODEL_ID);
                setNewTemplateImages([]);
                setNewTemplateImagePreviews([]);
                setNewTemplateIsAssessment(false);
                setNewTemplateAssessmentSource('topic');
                setNewTemplateAssessmentTopic('');
                setNewTemplateAssessmentTemplateIds('');
                setNewTemplateQuestionCount('');
              }
            }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Lesson</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  if (!isCreatingTemplate) {
                    setShowAddTemplateModal(false);
                    setNewTemplateModel(OPENAI_MODEL_ID);
                    setNewTemplateImages([]);
                    setNewTemplateImagePreviews([]);
                    setNewTemplateIsAssessment(false);
                    setNewTemplateAssessmentSource('topic');
                    setNewTemplateAssessmentTopic('');
                    setNewTemplateAssessmentTemplateIds('');
                    setNewTemplateQuestionCount('');
                  }
                }}
                disabled={isCreatingTemplate}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
                    <>
                      {/* ── Assessment toggle — FIRST ── */}
                  <div className="form-group">
                    <div style={{
                      background: newTemplateIsAssessment ? 'linear-gradient(135deg, #fef3c7 0%, #ede9fe 100%)' : '#f8fafc',
                      border: newTemplateIsAssessment ? '1.5px solid #a78bfa' : '1.5px solid #e2e8f0',
                      borderRadius: '12px', padding: '14px', transition: 'all 0.2s ease',
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
                          background: newTemplateIsAssessment ? '#6366f1' : '#e2e8f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.2s',
                        }}>
                          {newTemplateIsAssessment && <span style={{ color: '#fff', fontSize: '13px' }}>✓</span>}
                          <input type="checkbox" checked={newTemplateIsAssessment}
                            onChange={e => {
                              setNewTemplateIsAssessment(e.target.checked);
                              setNewTemplateAssessmentSource('topic');
                              setNewTemplateAssessmentTopic('');
                              setNewTemplateAssessmentTemplateIds('');
                              setNewTemplateQuestionCount('');
                            }}
                            disabled={isCreatingTemplate}
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                          />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: newTemplateIsAssessment ? '#4c1d95' : '#374151' }}>
                            📝 Assessment Only
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            Quiz-only — questions derived from objectives, no teaching slides
                          </div>
                        </div>
                      </label>
                      {newTemplateIsAssessment && (
                        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid rgba(167,139,250,0.3)', paddingTop: '14px' }}>
                          <div style={{ display: 'flex', gap: '6px', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
                            <button type="button" onClick={() => setNewTemplateAssessmentSource('topic')} disabled={isCreatingTemplate}
                              style={{ flex: 1, padding: '7px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'topic' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'topic' ? '#d97706' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'topic' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                              🎯 By Topic
                            </button>
                            <button type="button" onClick={() => setNewTemplateAssessmentSource('template_ids')} disabled={isCreatingTemplate}
                              style={{ flex: 1, padding: '7px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'template_ids' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'template_ids' ? '#6366f1' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'template_ids' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                              📚 From Lessons
                            </button>
                            <button type="button" onClick={() => setNewTemplateAssessmentSource('uploaded_file')} disabled={isCreatingTemplate}
                              style={{ flex: 1, padding: '7px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'uploaded_file' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'uploaded_file' ? '#0891b2' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'uploaded_file' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                              📎 From File
                            </button>
                          </div>
                          {newTemplateAssessmentSource === 'topic' ? (
                            <input value={newTemplateAssessmentTopic} onChange={e => setNewTemplateAssessmentTopic(e.target.value)}
                              placeholder="e.g. Workplace Safety, Python Basics…" disabled={isCreatingTemplate}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #ddd6fe', fontSize: '13px', boxSizing: 'border-box', outline: 'none', background: '#fafafa' }} />
                          ) : newTemplateAssessmentSource === 'template_ids' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div style={{ border: '1.5px solid #ddd6fe', borderRadius: '8px', background: '#fafafa', maxHeight: '160px', overflowY: 'auto' }}>
                                {availableTemplates.filter(t => !t.id.startsWith('creating-')).map(t => {
                                  const selected = newTemplateAssessmentTemplateIds.split(',').map(s => s.trim()).filter(Boolean).includes(t.id);
                                  return (
                                    <div key={t.id} onClick={() => { if (isCreatingTemplate) return;
                                        const ids = newTemplateAssessmentTemplateIds.split(',').map(s => s.trim()).filter(Boolean);
                                        setNewTemplateAssessmentTemplateIds((selected ? ids.filter(id => id !== t.id) : [...ids, t.id]).join(', ')); }}
                                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px',
                                        background: selected ? '#ede9fe' : 'transparent', borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' }}>
                                      <span style={{ width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0, background: selected ? '#6366f1' : '#e2e8f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff' }}>
                                        {selected ? '✓' : ''}
                                      </span>
                                      <span style={{ color: '#374151', fontWeight: selected ? 600 : 400 }}>{t.name}</span>
                                      <span style={{ color: '#9ca3af', marginLeft: 'auto', fontSize: '11px' }}>{t.id}</span>
                                    </div>
                                  );
                                })}
                              </div>
                              <small style={{ color: '#9ca3af', fontSize: '11px' }}>Smart objectives are extracted from selected lessons to shape the questions.</small>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <input ref={assessmentFileInputRef} type="file"
                                accept=".pdf,.docx,.pptx,.csv,.xlsx,.xls"
                                onChange={handleAssessmentFileChange}
                                disabled={isCreatingTemplate || assessmentFileUploading}
                                style={{ display: 'none' }} />
                              {newTemplateAssessmentFile ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #a5f3fc', background: '#ecfeff', fontSize: '13px' }}>
                                  <span style={{ fontSize: '16px' }}>📄</span>
                                  <span style={{ flex: 1, color: '#0e7490', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {assessmentFileUploading ? 'Uploading…' : newTemplateAssessmentFile.name}
                                  </span>
                                  {!assessmentFileUploading && (
                                    <button type="button" onClick={removeAssessmentFile} disabled={isCreatingTemplate}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0 2px', fontSize: '14px' }}>✕</button>
                                  )}
                                </div>
                              ) : (
                                <button type="button" onClick={() => assessmentFileInputRef.current?.click()}
                                  disabled={isCreatingTemplate || assessmentFileUploading}
                                  style={{ padding: '9px 12px', borderRadius: '8px', border: '1.5px dashed #7dd3fc', background: '#f0f9ff', color: '#0369a1', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>
                                  Click to upload PDF, Word, PowerPoint, or CSV
                                </button>
                              )}
                              <small style={{ color: '#9ca3af', fontSize: '11px' }}>Questions are generated from the document content.</small>
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>Number of questions:</label>
                            <input type="number" min={5} max={50} value={newTemplateQuestionCount} onChange={e => setNewTemplateQuestionCount(e.target.value)}
                              placeholder="auto" disabled={isCreatingTemplate}
                              style={{ width: '80px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }} />
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Blank = 2× objectives</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                      {/* ── Description — only for non-assessment ── */}
                      {!newTemplateIsAssessment && (
                      <div className="form-group">
                      <label htmlFor="template-description">Vibe your lesson here</label>
                      <textarea
                        id="template-description"
                        value={newTemplateDescription}
                        onChange={(e) => setNewTemplateDescription(e.target.value)}
                        placeholder="Describe the lesson you want (topic, level, objectives, interactions, tone)."
                        disabled={isCreatingTemplate}
                        className="form-textarea"
                        rows={5}
                      />
                      </div>
                      )}
                  <div className="form-group">
                    {newTemplateIsAssessment ? (
                      <>
                        <label htmlFor="template-model">AI Model:</label>
                        {codexModels.models.length > 0 ? (
                          <select
                            id="template-model"
                            value={newTemplateModel || codexModels.default}
                            onChange={(e) => setNewTemplateModel(e.target.value)}
                            disabled={isCreatingTemplate}
                            className="form-input"
                            style={{
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid var(--border)',
                              backgroundColor: 'var(--panel-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px',
                              cursor: isCreatingTemplate ? 'not-allowed' : 'pointer',
                              outline: 'none',
                              transition: 'border-color 0.2s'
                            }}
                          >
                            {codexModels.models.map(m => (
                              <option key={m.id} value={m.id}>{m.label}</option>
                            ))}
                          </select>
                        ) : (
                          <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '6px' }}>
                            No models available — set at least one API key in server <code>.env</code>
                          </div>
                        )}
                        <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                          Select which AI model to use for generating your assessment
                        </small>
                      </>
                    ) : (
                      <>
                        <label htmlFor="codex-model">AI Model:</label>
                        {codexModels.models.length > 0 ? (
                          <select
                            id="codex-model"
                            value={newTemplateModel || codexModels.default}
                            onChange={(e) => setNewTemplateModel(e.target.value)}
                            disabled={isCreatingTemplate}
                            className="form-input"
                            style={{
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid var(--border)',
                              backgroundColor: 'var(--panel-bg)',
                              color: 'var(--text-primary)',
                              fontSize: '14px',
                              cursor: isCreatingTemplate ? 'not-allowed' : 'pointer',
                              outline: 'none',
                              transition: 'border-color 0.2s'
                            }}
                          >
                            {codexModels.models.map(m => (
                              <option key={m.id} value={m.id}>{m.label}</option>
                            ))}
                          </select>
                        ) : (
                          <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '6px' }}>
                            No models available — set at least one API key in server <code>.env</code>
                          </div>
                        )}
                        <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                          Select which AI model to use for generating your lesson
                        </small>
                      </>
                    )}
                  </div>
                      {/* ── OLD ASSESSMENT TOGGLE PLACEHOLDER (removed, moved to top) ── */}
                  <div className="form-group" style={{display:'none'}}>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
                          background: newTemplateIsAssessment ? '#6366f1' : '#e2e8f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.2s',
                        }}>
                          {newTemplateIsAssessment && <span style={{ color: '#fff', fontSize: '13px' }}>✓</span>}
                          <input
                            type="checkbox"
                            checked={newTemplateIsAssessment}
                            onChange={e => {
                              setNewTemplateIsAssessment(e.target.checked);
                              setNewTemplateAssessmentSource('topic');
                              setNewTemplateAssessmentTopic('');
                              setNewTemplateAssessmentTemplateIds('');
                              setNewTemplateQuestionCount('');
                            }}
                            disabled={isCreatingTemplate}
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                          />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: newTemplateIsAssessment ? '#4c1d95' : '#374151' }}>
                            📝 Assessment Only
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            Quiz-only lesson — no teaching slides
                          </div>
                        </div>
                      </label>

                      {newTemplateIsAssessment && (
                        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid rgba(167,139,250,0.3)', paddingTop: '14px' }}>
                          
                          {/* Source toggle */}
                          <div style={{ display: 'flex', gap: '6px', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
                            <button
                              type="button"
                              onClick={() => setNewTemplateAssessmentSource('topic')}
                              disabled={isCreatingTemplate}
                              style={{
                                flex: 1, padding: '7px', borderRadius: '6px', border: 'none',
                                cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'topic' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'topic' ? '#d97706' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'topic' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.15s',
                              }}
                            >
                              🎯 By Topic
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewTemplateAssessmentSource('template_ids')}
                              disabled={isCreatingTemplate}
                              style={{
                                flex: 1, padding: '7px', borderRadius: '6px', border: 'none',
                                cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                background: newTemplateAssessmentSource === 'template_ids' ? '#ffffff' : 'transparent',
                                color: newTemplateAssessmentSource === 'template_ids' ? '#6366f1' : '#6b7280',
                                boxShadow: newTemplateAssessmentSource === 'template_ids' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.15s',
                              }}
                            >
                              📚 From Lessons
                            </button>
                          </div>

                          {/* Conditional input */}
                          {newTemplateAssessmentSource === 'topic' ? (
                            <input
                              value={newTemplateAssessmentTopic}
                              onChange={e => setNewTemplateAssessmentTopic(e.target.value)}
                              placeholder="e.g. Workplace Safety, Python Basics…"
                              disabled={isCreatingTemplate}
                              style={{
                                width: '100%', padding: '9px 12px', borderRadius: '8px',
                                border: '1.5px solid #ddd6fe', fontSize: '13px',
                                boxSizing: 'border-box', outline: 'none',
                                background: '#fafafa',
                              }}
                            />
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div style={{
                                border: '1.5px solid #ddd6fe', borderRadius: '8px',
                                background: '#fafafa', maxHeight: '160px', overflowY: 'auto',
                              }}>
                                {availableTemplates.filter(t => !t.id.startsWith('creating-')).map(t => {
                                  const selected = newTemplateAssessmentTemplateIds
                                    .split(',').map(s => s.trim()).filter(Boolean).includes(t.id);
                                  return (
                                    <div
                                      key={t.id}
                                      onClick={() => {
                                        if (isCreatingTemplate) return;
                                        const ids = newTemplateAssessmentTemplateIds
                                          .split(',').map(s => s.trim()).filter(Boolean);
                                        const next = selected
                                          ? ids.filter(id => id !== t.id)
                                          : [...ids, t.id];
                                        setNewTemplateAssessmentTemplateIds(next.join(', '));
                                      }}
                                      style={{
                                        padding: '8px 12px', cursor: 'pointer', fontSize: '12px',
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        background: selected ? '#ede9fe' : 'transparent',
                                        borderBottom: '1px solid #f1f5f9',
                                        transition: 'background 0.1s',
                                      }}
                                    >
                                      <span style={{
                                        width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                                        background: selected ? '#6366f1' : '#e2e8f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '10px', color: '#fff',
                                      }}>
                                        {selected ? '✓' : ''}
                                      </span>
                                      <span style={{ color: '#374151', fontWeight: selected ? 600 : 400 }}>{t.name}</span>
                                      <span style={{ color: '#9ca3af', marginLeft: 'auto', fontSize: '11px' }}>{t.id}</span>
                                    </div>
                                  );
                                })}
                              </div>
                              <small style={{ color: '#9ca3af', fontSize: '11px' }}>
                                Smart objectives are extracted from selected lessons to shape the questions.
                              </small>
                            </div>
                          )}

                          {/* Question count */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                              Number of questions:
                            </label>
                            <input
                              type="number"
                              min={5} max={50}
                              value={newTemplateQuestionCount}
                              onChange={e => setNewTemplateQuestionCount(e.target.value)}
                              placeholder="12–15"
                              disabled={isCreatingTemplate}
                              style={{
                                width: '80px', padding: '6px 8px', borderRadius: '6px',
                                border: '1px solid #d1d5db', fontSize: '13px',
                              }}
                            />
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                              Leave blank for default (12–15)
                            </span>
                          </div>

                        </div>
                      )}
                    </div>
                  </div>
                  {!newTemplateIsAssessment && <div className="form-group">
                    <label htmlFor="template-image">Reference Images (Optional, up to 6):</label>
                    <input
                      type="file"
                      id="template-image"
                      ref={templateImageInputRef}
                      accept="image/*"
                      multiple
                      onChange={handleTemplateImageSelect}
                      disabled={isCreatingTemplate}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => templateImageInputRef.current?.click()}
                        className="image-upload-button"
                        disabled={isCreatingTemplate}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                      >
                        🖼️ Choose Image(s)
                      </button>
                      {newTemplateImagePreviews.length > 0 && newTemplateImagePreviews.map((src, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img
                            src={src}
                            alt={`Preview ${idx + 1}`}
                            style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <button
                            type="button"
                            onClick={() => removeTemplateImage(idx)}
                            disabled={isCreatingTemplate}
                            style={{ position: 'absolute', top: '2px', right: '2px', padding: '2px 6px', cursor: 'pointer', fontSize: '14px' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Upload one or more images as design inspiration for the template
                    </small>
                  </div>}
                  {!newTemplateIsAssessment && <div className="form-group">
                    <label htmlFor="template-file">Data File (CSV/Excel/PDF/DOCX/PPTX) - Optional:</label>
                    <input
                      type="file"
                      id="template-file"
                      ref={templateFileInputRef}
                      accept=".csv,.xlsx,.xls,.pdf,.docx,.pptx"
                      onChange={handleTemplateFileSelect}
                      disabled={isCreatingTemplate}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => templateFileInputRef.current?.click()}
                        className="image-upload-button"
                        disabled={isCreatingTemplate}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                      >
                        📁 Choose File
                      </button>
                      {newTemplateFile && (
                        <>
                          <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                            📄 {newTemplateFile.name}
                          </span>
                          <button
                            type="button"
                            onClick={removeTemplateFile}
                            disabled={isCreatingTemplate}
                            style={{ padding: '4px 8px', cursor: 'pointer' }}
                          >
                            × Remove
                          </button>
                        </>
                      )}
                    </div>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Upload a CSV, Excel, PDF, Word, or PowerPoint file to incorporate data into the template
                    </small>
                  </div>}
                      <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={() => {
                        setShowAddTemplateModal(false);
                        setNewTemplateModel(OPENAI_MODEL_ID);
                        setNewTemplateImages([]);
                        setNewTemplateImagePreviews([]);
                        setNewTemplateIsAssessment(false);
                        setNewTemplateAssessmentSource('topic');
                        setNewTemplateAssessmentTopic('');
                        setNewTemplateAssessmentTemplateIds('');
                        setNewTemplateQuestionCount('');
                      }}
                      disabled={isCreatingTemplate}
                    >
                      Cancel
                    </button>
                        <button
                          className="modal-button primary"
                          onClick={handleCreateTemplate}
                          disabled={isCreatingTemplate ||
                            (!newTemplateIsAssessment && !newTemplateDescription.trim()) ||
                            (newTemplateIsAssessment && newTemplateAssessmentSource === 'topic' && !newTemplateAssessmentTopic.trim()) ||
                            (newTemplateIsAssessment && newTemplateAssessmentSource === 'template_ids' && !newTemplateAssessmentTemplateIds.trim())}
                        >
                          Create lesson
                        </button>
                      </div>
                    </>
            </div>
          </div>
        </div>
      )}

          {/* Copy lesson Modal */}
          {showCopyModal && (
        <div className="modal-overlay" onClick={() => {
          if (!isCopying) {
            setShowCopyModal(false);
            setCopyLessonName('');
          }
        }}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Copy lesson</h2>
                  <button 
                    className="modal-close"
                onClick={() => {
                  if (!isCopying) {
                    setShowCopyModal(false);
                    setCopyLessonName('');
                  }
                }}
                    disabled={isCopying}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
                    Create a copy of this lesson that you can edit. The original lesson will remain unchanged.
                  </p>
                  <div className="form-group">
                    <label htmlFor="copy-lesson-name">New Lesson Name:</label>
                    <input
                      id="copy-lesson-name"
                      type="text"
                      value={copyLessonName}
                      onChange={(e) => setCopyLessonName(e.target.value)}
                      placeholder="Enter a name for the copied lesson"
                      disabled={isCopying}
                      className="form-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isCopying && copyLessonName.trim()) {
                      handleCopyLesson();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
                  <button
                className="modal-button cancel"
                onClick={() => {
                  setShowCopyModal(false);
                  setCopyLessonName('');
                }}
                disabled={isCopying}
              >
                Cancel
              </button>
              <button
                className="modal-button primary"
                onClick={handleCopyLesson}
                    disabled={isCopying || !copyLessonName.trim()}
                  >
                    {isCopying ? 'Copying...' : 'Copy lesson'}
                  </button>
                </div>
              </div>
            </div>
          )}

      {/* QA Feedback Modal */}
          {showQaModal && (
            <div className="modal-overlay" onClick={() => setShowQaModal(false)}>
              <div className="modal-content qa-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>QA Feedback Report</h2>
                  <button className="modal-close" onClick={() => setShowQaModal(false)}>×</button>
                </div>
                <div className="modal-body" style={{ textAlign: 'left' }}>
                  {isQaLoading ? (
                    <div className="qa-loading-container">
                      <div className="deploy-spinner large"></div>
                      <p>Analyzing lesson performance...</p>
                    </div>
                  ) : qaReportData?.status === 'generating' ? (
                    <div className="qa-loading-container">
                      <div className="deploy-spinner large"></div>
                      <p>Report is being generated in the background. Please come back in a minute.</p>
                    </div>
                  ) : qaReportData?.error ? (
                    <div className="qa-error-message">
                      <p>{qaReportData.error}</p>
                      {qaReportData.status === "not_found" && ['manager', 'creator'].includes(currentLessonRole) && (
                        <button
                          className="modal-button primary"
                          style={{ marginTop: '15px' }}
                          onClick={async () => {
                            setIsQaLoading(true);
                            try {
                              await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/evaluate`, withAuthFetchOptions({ method: 'POST' }));
                              setQaReportData({ status: 'generating', error: "Evaluation started in background. Please check back in a minute." });
                              startQaPolling(selectedTemplate);
                            } catch (err) {
                              setIsQaLoading(false);
                              setQaReportData({ status: 'error', error: "Failed to start evaluation." });
                            }
                          }}
                        >
                          Run Evaluation Now
                        </button>
                      )}
                    </div>
                  ) : qaReportData ? (
                    <div className="qa-report-container">
                      <div className={`qa-status-badge ${qaReportData.success ? 'passed' : 'failed'}`}>
                        <strong>{qaReportData.success ? '✅ Passed' : '❌ Failed'}</strong>
                      </div>
                      {(() => {
                        const currentScore = qaReportData.overall_score || 0;
                        const fixedPoints = Object.values(qaReportData.sections || {}).reduce((sum, sec) => {
                          const fixedImps = (sec.improvements || []).filter(imp => imp._fixed);
                          return sum + fixedImps.reduce((s, imp) => s + (imp.estimated_points || 0), 0);
                        }, 0);
                        const originalScore = Math.max(0, currentScore - fixedPoints);

                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', margin: '8px 0 4px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em', marginBottom: '6px', color: 'var(--muted, #94a3b8)' }}>
                                <span>Current Score: <strong style={{ color: 'var(--text-color, #e2e8f0)' }}>{currentScore}/100</strong>
                                  {fixedPoints > 0 && <span style={{ marginLeft: '8px', color: '#22c55e', fontSize: '0.9em' }}>(Original: {originalScore}, +{fixedPoints} pts added)</span>}
                                </span>
                              </div>
                              <div style={{ height: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                                <div style={{ position: 'relative', width: `${currentScore}%`, height: '100%', background: currentScore >= 80 ? '#22c55e' : currentScore >= 50 ? '#eab308' : '#ef4444', borderRadius: '4px', transition: 'width 0.5s ease' }}></div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {qaReportData.summary && (
                        <div className="qa-section">
                          <h3>Summary</h3>
                          <p className="qa-summary-text" dangerouslySetInnerHTML={{ __html: qaReportData.summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}></p>
                        </div>
                      )}
                      {qaReportData.sections && Object.keys(qaReportData.sections).length > 0 && (
                        <div className="qa-section">
                          <h3>Section Scores</h3>
                          <div style={{ display: 'grid', gap: '10px' }}>
                            {Object.entries(qaReportData.sections).map(([key, sec]) => {
                              const label = { educational_structure: 'Educational Structure', interactivity: 'Interactivity', ui_ux: 'UI/UX & Readability', images_and_media: 'Images & Media', content_accuracy: 'Content Accuracy' }[key] || key;
                              const pct = sec.max > 0 ? Math.round((sec.score / sec.max) * 100) : 0;
                              const barColor = pct >= 80 ? '#22c55e' : pct >= 50 ? '#eab308' : '#ef4444';
                              return (
                                <div key={key} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '10px 14px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontWeight: 600, fontSize: '0.9em' }}>
                                    <span>{label}</span>
                                    <span style={{ color: barColor }}>{sec.score}/{sec.max}</span>
                                  </div>
                                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '3px', transition: 'width 0.5s ease' }}></div>
                                  </div>
                                  {sec.section_summary && (
                                    <p style={{ margin: '0 0 8px 0', fontSize: '0.85em', fontStyle: 'italic', color: 'var(--text-color, #e2e8f0)' }}>
                                      {sec.section_summary}
                                    </p>
                                  )}
                                  {sec.findings && sec.findings.length > 0 && (
                                    <ul style={{ margin: '6px 0 0 0', padding: '0 0 0 16px', fontSize: '0.82em', color: 'var(--muted, #94a3b8)' }}>
                                      {sec.findings.map((f, i) => <li key={i} style={{ marginBottom: '2px' }}>{f}</li>)}
                                    </ul>
                                  )}
                                  {sec.improvements && sec.improvements.length > 0 && ['manager', 'creator'].includes(currentLessonRole) && (
                                    <div style={{ marginTop: '10px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: '8px', padding: '10px 12px' }}>
                                      <div style={{ fontSize: '0.82em', fontWeight: 600, color: '#22c55e', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>💡</span> How to improve this score
                                      </div>
                                      {sec.improvements.map((imp) => (
                                        <div key={imp.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', gap: '8px', opacity: imp._fixed ? 0.6 : 1 }}>
                                          <div style={{ flex: 1, fontSize: '0.82em' }}>
                                            <span style={{ marginRight: '6px' }}>{imp.type === 'fix' ? '🔧' : '✨'}</span>
                                            {imp.description}
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                            <span style={{ fontSize: '0.75em', fontWeight: 700, color: '#22c55e', background: 'rgba(34,197,94,0.12)', padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>+{imp.estimated_points} pts</span>
                                            <button
                                              className="qa-fix-button"
                                              style={{ fontSize: '0.75em', padding: '3px 10px', ...(imp._fixed ? { background: 'transparent', cursor: 'default', color: '#22c55e', border: '1px solid #22c55e' } : {}) }}
                                              disabled={fixingImprovementId !== null || batchFixingSection !== null || imp._fixed}
                                              onClick={async () => {
                                                setFixingImprovementId(imp.id);
                                                const fixTemplate = selectedTemplate;
                                                try {
                                                  const res = await fetch(
                                                    `${API_URL}/board/templates/${encodeURIComponent(fixTemplate)}/qa-fix`,
                                                    withAuthFetchOptions({
                                                      method: 'POST',
                                                      headers: { 'Content-Type': 'application/json' },
                                                      body: JSON.stringify({
                                                        fix_prompt: imp.fix_prompt,
                                                        issue_id: imp.id,
                                                        model: availableTemplates.find(t => t.id === fixTemplate)?.default_model || 'gpt-5.4-mini'
                                                      }),
                                                    })
                                                  );
                                                  const startData = await res.json();
                                                  if (!startData.success) { setFixingImprovementId(null); return; }
                                                  const pollInterval = setInterval(async () => {
                                                    try {
                                                      const statusRes = await fetch(
                                                        `${API_URL}/board/templates/${encodeURIComponent(fixTemplate)}/qa-fix-status/${encodeURIComponent(imp.id)}`,
                                                        withAuthFetchOptions()
                                                      );
                                                      const statusData = await statusRes.json();
                                                      if (statusData.status === 'done') {
                                                        clearInterval(pollInterval);
                                                        if (selectedTemplateRef.current === fixTemplate && iframeRef.current) {
                                                          iframeRef.current.src = buildIframeSrc(fixTemplate, Date.now());
                                                        }
                                                        setQaReportData(prev => {
                                                          if (!prev) return prev;
                                                          const newSections = { ...prev.sections };
                                                          let ptsAdded = 0;
                                                          if (newSections[key]) {
                                                            const targetImp = (newSections[key].improvements || []).find(i => i.id === imp.id);
                                                            ptsAdded = targetImp && !targetImp._fixed ? (targetImp.estimated_points || 0) : 0;
                                                            newSections[key] = { ...newSections[key], improvements: (newSections[key].improvements || []).map(i => i.id === imp.id ? { ...i, _fixed: true } : i) };
                                                            newSections[key].score = Math.min(newSections[key].max || 20, (newSections[key].score || 0) + ptsAdded);
                                                          }
                                                          const newScore = Math.min(100, (prev.overall_score || 0) + ptsAdded);
                                                          
                                                          // Sync with flat issues list
                                                          const newIssues = (prev.issues || []).map(issue => {
                                                            if (issue.related_improvement_id === imp.id || (imp.related_issue_ids && imp.related_issue_ids.includes(issue.id))) {
                                                              return { ...issue, _fixed: true };
                                                            }
                                                            return issue;
                                                          });

                                                          const updatedReport = { ...prev, overall_score: newScore, sections: newSections, issues: newIssues };
                                                          
                                                          // Real-time success re-evaluation
                                                          const hasUnfixedMajor = updatedReport.issues.some(i => !i._fixed && (i.severity === 'critical' || i.severity === 'major'));
                                                          updatedReport.success = updatedReport.overall_score >= 80 && !hasUnfixedMajor;
                                                          
                                                          return updatedReport;
                                                        });
                                                        setFixingImprovementId(null);
                                                        window.dispatchEvent(new CustomEvent('qaFixApplied', {
                                                          detail: { 
                                                            template: fixTemplate, 
                                                            userText: imp.fix_prompt, 
                                                            advisorText: statusData.ai_response_text || "Fix applied successfully."
                                                          }
                                                        }));
                                                        window.dispatchEvent(new CustomEvent('qa_fix_applied'));
                                                      } else if (statusData.status === 'error') {
                                                        clearInterval(pollInterval);
                                                        setFixingImprovementId(null);
                                                      }
                                                    } catch { }
                                                  }, 3000);
                                                } catch { setFixingImprovementId(null); }
                                              }}
                                            >
                                              {imp._fixed ? '✅ Fixed' : fixingImprovementId === imp.id ? (
                                                <><div className="deploy-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px', display: 'inline-block', marginRight: '4px' }}></div>Fixing...</>
                                              ) : 'Fix'}
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                      {sec.improvements.filter(imp => !imp._fixed).length > 1 && (
                                        <div style={{ marginTop: '8px', textAlign: 'right' }}>
                                          <button
                                            className="qa-fix-button"
                                            style={{ fontSize: '0.78em', padding: '4px 14px', background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}
                                            disabled={batchFixingSection !== null || fixingImprovementId !== null}
                                            onClick={async () => {
                                              setBatchFixingSection(key);
                                              setBatchFixProgress({ total: sec.improvements.filter(i => !i._fixed).length, completed: 0 });
                                              const fixTemplate = selectedTemplate;
                                              try {
                                                const res = await fetch(
                                                  `${API_URL}/board/templates/${encodeURIComponent(fixTemplate)}/qa-batch-fix`,
                                                  withAuthFetchOptions({
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({
                                                      section_key: key,
                                                      improvements: sec.improvements.filter(i => !i._fixed).map(i => ({ id: i.id, fix_prompt: i.fix_prompt })),
                                                      model: availableTemplates.find(t => t.id === fixTemplate)?.default_model || 'gpt-5.4-mini'
                                                    }),
                                                  })
                                                );
                                                const startData = await res.json();
                                                if (!startData.success) { setBatchFixingSection(null); setBatchFixProgress(null); return; }

                                                const pollInterval = setInterval(async () => {
                                                  try {
                                                    const statusRes = await fetch(
                                                      `${API_URL}/board/templates/${encodeURIComponent(fixTemplate)}/qa-batch-fix-status/${encodeURIComponent(key)}`,
                                                      withAuthFetchOptions()
                                                    );
                                                    const statusData = await statusRes.json();
                                                    if (statusData.status === 'fixing') {
                                                      setBatchFixProgress({ total: statusData.total, completed: statusData.completed });
                                                    } else if (statusData.status === 'done') {
                                                      clearInterval(pollInterval);
                                                      if (selectedTemplateRef.current === fixTemplate && iframeRef.current) {
                                                        iframeRef.current.src = buildIframeSrc(fixTemplate, Date.now());
                                                      }
                                                      setQaReportData(prev => {
                                                        if (!prev) return prev;
                                                        const newSections = { ...prev.sections };
                                                        let ptsAdded = 0;
                                                        const fixedImpIds = [];
                                                        const allRelatedIssueIds = [];

                                                        if (newSections[key]) {
                                                          const targetImps = (newSections[key].improvements || []).filter(i => !i._fixed);
                                                          ptsAdded = targetImps.reduce((sum, imp) => sum + (imp.estimated_points || 0), 0);
                                                          targetImps.forEach(i => {
                                                            fixedImpIds.push(i.id);
                                                            if (i.related_issue_ids) allRelatedIssueIds.push(...i.related_issue_ids);
                                                          });
                                                          newSections[key] = { ...newSections[key], improvements: (newSections[key].improvements || []).map(i => ({ ...i, _fixed: true })) };
                                                          newSections[key].score = Math.min(newSections[key].max || 20, (newSections[key].score || 0) + ptsAdded);
                                                        }
                                                        const newScore = Math.min(100, (prev.overall_score || 0) + ptsAdded);

                                                        // Sync with flat issues list
                                                        const newIssues = (prev.issues || []).map(issue => {
                                                          if (fixedImpIds.includes(issue.related_improvement_id) || allRelatedIssueIds.includes(issue.id)) {
                                                            return { ...issue, _fixed: true };
                                                          }
                                                          return issue;
                                                        });

                                                        const updatedReport = { ...prev, overall_score: newScore, sections: newSections, issues: newIssues };
                                                        
                                                        // Real-time success re-evaluation
                                                        const hasUnfixedMajor = updatedReport.issues.some(i => !i._fixed && (i.severity === 'critical' || i.severity === 'major'));
                                                        updatedReport.success = updatedReport.overall_score >= 80 && !hasUnfixedMajor;
                                                        
                                                        return updatedReport;
                                                      });
                                                      setBatchFixingSection(null);
                                                      setBatchFixProgress(null);
                                                      const sectionLabel = { educational_structure: 'Educational Structure', interactivity: 'Interactivity', ui_ux: 'UI/UX & Readability', images_and_media: 'Images & Media', content_accuracy: 'Content Accuracy' }[key] || key;
                                                      const fixedImprovements = sec.improvements.filter(i => !i._fixed);
                                                      const fixingCount = fixedImprovements.length;
                                                      const issuesList = fixedImprovements.slice(0, 3).map(i => `• ${i.description}`).join('\n') + (fixingCount > 3 ? `\n• ...and ${fixingCount - 3} more` : '');
                                                      
                                                      window.dispatchEvent(new CustomEvent('qaFixApplied', {
                                                        detail: { 
                                                          template: fixTemplate, 
                                                          userText: `Apply batch fixes for section: ${sectionLabel}.\n\nImprovements applying:\n${issuesList}`, 
                                                          advisorText: `Successfully applied ${statusData.completed || fixingCount} fixes for the ${sectionLabel} section.`
                                                        }
                                                      }));
                                                      window.dispatchEvent(new CustomEvent('qa_fix_applied'));
                                                    } else if (statusData.status === 'error') {
                                                      clearInterval(pollInterval);
                                                      setBatchFixingSection(null);
                                                      setBatchFixProgress(null);
                                                    }
                                                  } catch { }
                                                }, 3000);
                                              } catch { setBatchFixingSection(null); setBatchFixProgress(null); }
                                            }}
                                          >
                                            {batchFixingSection === key ? (
                                              <><div className="deploy-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px', display: 'inline-block', marginRight: '4px' }}></div>Fixing {Math.min((batchFixProgress?.completed || 0) + 1, batchFixProgress?.total || 1)}/{batchFixProgress?.total || '...'}...</>
                                            ) : `Fix All Section Issues (${sec.improvements.filter(i => !i._fixed).length})`}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {qaReportData.issues && qaReportData.issues.length > 0 && (
                        <div className="qa-section">
                          <h3
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}
                            onClick={() => setIssuesCollapsed(prev => !prev)}
                          >
                            <span style={{ transition: 'transform 0.2s', transform: issuesCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
                            Issues Found ({qaReportData.issues.length})
                          </h3>
                          <div className="qa-issues-list" style={{ display: issuesCollapsed ? 'none' : 'block' }}>
                            {qaReportData.issues.map((issue) => {
                              const isNew = qaReportData.diff_from_previous?.new_ids?.includes(issue.id);
                              const isPersisted = qaReportData.diff_from_previous?.persisted_ids?.includes(issue.id);
                              return (
                                <div key={issue.id} className={`qa-issue-card severity-${issue.severity}`} style={{ opacity: issue._fixed ? 0.6 : 1, position: 'relative' }}>
                                  <div className="qa-issue-header" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                    <span className={`qa-severity-badge ${issue.severity}`}>
                                      {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}
                                    </span>
                                    {isNew && <span style={{ backgroundColor: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65em', fontWeight: 'bold' }}>NEW</span>}
                                    {isPersisted && <span style={{ backgroundColor: '#eab308', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65em', fontWeight: 'bold' }}>PERSISTED</span>}
                                    <span className="qa-issue-category">{issue.category ? issue.category.replace(/\|/g, " • ") : "Issue"}</span>
                                    {issue._fixed && (
                                      <span style={{ marginLeft: 'auto', backgroundColor: '#22c55e', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.7em', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ fontSize: '1.1em' }}>✓</span> Resolved
                                      </span>
                                    )}
                                  </div>
                                  <p className="qa-issue-description" style={{ textDecoration: issue._fixed ? 'line-through' : 'none', color: issue._fixed ? 'var(--muted)' : 'inherit', marginTop: '8px' }}>{issue.description}</p>
                                  {fixIssueError?.issueId === issue.id && (
                                    <p className="qa-fix-error">⚠️ {fixIssueError.message}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {qaReportData.diff_from_previous && qaReportData.diff_from_previous.resolved?.length > 0 && (
                        <div className="qa-section" style={{ marginTop: '20px' }}>
                          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#22c55e' }}>
                            <span>✅</span> Resolved Since Last Run ({qaReportData.diff_from_previous.resolved.length})
                          </h3>
                          <div className="qa-issues-list">
                            {qaReportData.diff_from_previous.resolved.map((issue) => (
                              <div key={issue.id} className={`qa-issue-card`} style={{ opacity: 0.7, borderLeft: '4px solid #22c55e' }}>
                                <div className="qa-issue-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ textDecoration: 'line-through', fontSize: '0.85em', color: 'var(--muted)' }}>
                                    {issue.category ? issue.category.replace(/\|/g, " • ") : "Issue"}
                                  </span>
                                  <span style={{ marginLeft: 'auto', color: '#22c55e', fontSize: '0.75em', fontWeight: 'bold' }}>
                                    Resolved
                                  </span>
                                </div>
                                <p className="qa-issue-description" style={{ textDecoration: 'line-through', color: 'var(--muted)', marginTop: '8px' }}>{issue.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {qaReportData.turns !== undefined && (
                        <div className="qa-section">
                          <h3>Evaluation Details</h3>
                          <p className="qa-details-text">Steps taken: {qaReportData.turns}</p>
                          {qaReportData.evaluated_at && <p className="qa-details-text" style={{ fontSize: '0.85em', color: 'var(--muted, #94a3b8)' }}>Evaluated: {new Date(qaReportData.evaluated_at).toLocaleString()}</p>}
                        </div>
                      )}
                      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button
                          className="modal-button"
                          style={{ background: '#334155', color: '#e2e8f0' }}
                          // AFTER
onClick={async () => {
  const res = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/qa-report/pdf`, {
    headers: { Authorization: `Bearer ${currentToken}` }
  });
  if (!res.ok) {
    const err = await res.json();
    alert(err.error || 'Failed to download PDF');
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `qa_report_${selectedTemplate}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}}
                        >
                          📄 Download PDF
                        </button>
                        {['manager', 'creator'].includes(currentLessonRole) && (
                          <button
                            className="modal-button primary"
                            onClick={async () => {
                              setIsQaLoading(true);
                              try {
                                await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/evaluate`, withAuthFetchOptions({ method: 'POST' }));
                                setQaReportData({ status: 'generating' });
                                startQaPolling(selectedTemplate);
                              } catch (err) {
                                setIsQaLoading(false);
                                setQaReportData({ status: 'error', error: "Failed to start evaluation." });
                              } 
                            }}
                          >
                            Regenerate QA Report
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p>No report data available.</p>
                  )}
                </div>
              </div>
            </div>
          )}

      {/* Code Preview Modal */}
      {showCodePreview && selectedTemplate && (
        <CodePreview
          templateId={selectedTemplate}
          isOwner={templateOwnership[selectedTemplate]?.can_edit ?? true}
          onClose={() => setShowCodePreview(false)}
        />
      )}

          {/* Delete Template Confirmation Modal */}
          {showDeleteModal && templateToDelete && (
        <div className="modal-overlay" onClick={() => !isDeletingTemplate && handleDeleteCancel()}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Delete lesson</h2>
                  <button
                className="modal-close"
                onClick={handleDeleteCancel}
                disabled={isDeletingTemplate}
                aria-disabled={isDeletingTemplate}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {deleteStep === 1 ? (
                <>
                  <div className="delete-warning">
                    <div className="warning-icon">!</div>
                    <h3>Are you sure?</h3>
                    <p>You are about to delete <strong>"{templateToDelete.name}"</strong>.</p>
                    <p>This action cannot be undone.</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={handleDeleteCancel}
                    >
                      Cancel
                    </button>
                    <button
                      className="modal-button danger"
                      onClick={handleDeleteConfirm}
                    >
                      Yes, Delete
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="delete-warning step2">
                    <div className="warning-icon">⚠️</div>
                    <h3>Final Confirmation</h3>
                    <p>This is your last chance to cancel.</p>
                        <p>Deleting <strong>"{templateToDelete.name}"</strong> will permanently remove:</p>
                        <ul className="delete-consequences">
                          <li>The lesson file</li>
                          <li>All associated chat history</li>
                          <li>All saved data for this lesson</li>
                        </ul>
                    {deleteModalDeploymentUrl && (
                      <div className="delete-worker-option" style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'rgba(255,80,80,0.08)', borderRadius: '8px', border: '1px solid rgba(255,80,80,0.25)' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', fontWeight: 500 }}>
                          <input
                            type="checkbox"
                            checked={deleteWorker}
                            onChange={e => setDeleteWorker(e.target.checked)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          Also delete the deployed Cloudflare Worker
                        </label>
                        <p style={{ margin: '0.35rem 0 0 1.6rem', fontSize: '0.8rem', opacity: 0.7 }}>
                          {deleteModalDeploymentUrl}
                        </p>
                      </div>
                    )}
                    <p className="final-warning">Are you absolutely sure?</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={handleDeleteCancel}
                      disabled={isDeletingTemplate}
                    >
                      Cancel
                    </button>
                    <button
                      className="modal-button danger"
                      onClick={handleDeleteConfirm}
                      disabled={isDeletingTemplate}
                    >
                      {isDeletingTemplate ? (
                        <>
                          <span className="button-loading-dots" style={{ marginRight: '6px' }}>
                            <span></span><span></span><span></span>
                          </span>
                          Deleting...
                        </>
                      ) : (
                        'Delete Forever'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
     
{!galleryViewMode && selectedTemplate && !['reviewer', 'final_approver', 'distributor'].includes(currentLessonRole) && (
  <InlineEditor
  templateId={selectedTemplate}
  isOwner={templateOwnership[selectedTemplate]?.can_edit ?? true}
  isEditMode={isEditMode}
  setIsEditMode={setIsEditMode}
  iframeRef={iframeRef}
  token={currentToken}
/>
)}

      </div> {/* main-pane */}
      {!isFullscreen && !chatMinimized && !chatDocked && !galleryViewMode && (
        <div
          className={`floating-chat ${chatMinimized ? 'minimized' : ''}`}
          style={{ left: 0, top: 0, transform: `translate(${chatPosition.x}px, ${chatPosition.y}px)` }}
        >
          <div
            className="floating-chat-header"
            onMouseDown={(e) => {
              chatDragRef.current.dragging = true;
              chatDragRef.current.offsetX = chatPosition.x - e.clientX;
              chatDragRef.current.offsetY = chatPosition.y - e.clientY;
            }}
          >
            <span className="floating-chat-title">Lesson Copilot</span>
            <div className="floating-chat-actions">
              <button
                className="icon-btn small"
                onClick={() => setChatMinimized(!chatMinimized)}
                title={chatMinimized ? 'Expand chat' : 'Minimize chat'}
              >
                <span aria-hidden>{chatMinimized ? '■' : '▁'}</span>
                <span className="sr-only">{chatMinimized ? 'Expand chat' : 'Minimize chat'}</span>
              </button>
              <button
                className="icon-btn small"
                onClick={() => {
                  setChatDocked(true);
                  setChatCollapsed(false);
                  setChatMinimized(true);
                }}
                title="Dock chat"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  <line x1="9" y1="3" x2="9" y2="21"></line>
                </svg>
                <span className="sr-only">Dock chat</span>
              </button>
            </div>
          </div>
          {!chatMinimized && (
            <div className="floating-chat-body">
              <div className="floating-chat-content">
                {selectedTemplate ? (
                      <ChatAdvisor
                        key={`chat-${selectedTemplate}`}
                        selectedTemplate={selectedTemplate}
                        isOwner={templateOwnership[selectedTemplate]?.can_edit ?? true}
                        isCreatingTemplate={isCreatingTemplate}
                        creatingTemplateDescription={creatingTemplateDescription}
                        pendingTemplateId={pendingTemplateId}
                        apiKeysConfig={apiKeysConfig}
                        defaultModelForTemplate={availableTemplates.find(t => t.id === selectedTemplate)?.default_model}
                      isEditMode={isEditMode}
                      setIsEditMode={setIsEditMode}
                      />
                ) : (
                  <div style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>It's empty</p>
                    <p>Select or create a lesson to chat with the copilot.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div> {/* layout-shell */}
    {currentRole === 'distributor' && (
  <DistributorDashboard
    token={currentToken}
    onAssignLearners={(lesson) => {
      setLearnerAssignLesson(lesson);
      setShowLearnerAssignModal(true);
    }}
    onDeploy={(lesson) => {
      setSelectedTemplate(lesson.template_id);
      setGalleryViewMode(false);
      // Trigger deploy
      setTimeout(() => handleDeployClick(), 500);
    }}
  />
)}

{showLearnerAssignModal && learnerAssignLesson && (
  <LearnerAssignModal
    lesson={learnerAssignLesson}
    token={currentToken}
    onClose={() => { setShowLearnerAssignModal(false); setLearnerAssignLesson(null); }}
  />
)}

    <MyTasks
  token={currentToken}
  role={currentRole}
  onStartLesson={(task) => {
    console.log('[MyTasks] Starting lesson for task:', task.title);
    setNewTemplateName(task.title);
    setNewTemplateDescription(task.description || '');
    // Pre-fill files from backlog
    if (task.reference_image_previews?.length > 0) {
      setNewTemplateImagePreviews(task.reference_image_previews);
      setNewTemplateImages(task.reference_image_previews.map((_, i) => ({ name: `ref_image_${i}` })));
    }
    if (task.data_file_path) {
      setNewTemplateFilePath(task.data_file_path);
      setNewTemplateFile({ name: task.data_file_path.split('/').pop() });
    }
    setNewTemplateIsAssessment(task.is_assessment || false);
    setNewTemplateAssessmentSource(task.assessment_source || 'topic');
    setNewTemplateAssessmentTopic(task.assessment_topic || '');
    setNewTemplateAssessmentTemplateIds((task.assessment_template_ids || []).join(', '));
    setNewTemplateQuestionCount(task.question_count ? String(task.question_count) : '');
    setShowAddTemplateModal(true);
    window.__pendingBacklogId = task.backlog_id;
  }}
/>
    </div>
  );
}
function LearnerAssignModal({ lesson, token, onClose }) {
  const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  const [learnerUsers, setLearnerUsers] = useState([]);
  const [learnerIds, setLearnerIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Fetch learner role users
    fetch(`${API_URL}/distributor/learners`, { headers })
      .then(r => r.json())
      .then(data => { if (data?.success) setLearnerUsers(data.users || []); })
      .catch(() => {});

    // Fetch existing assignments
    fetch(`${API_URL}/admin/lessons/${lesson.template_id}/learners`, { headers })
      .then(r => r.json())
      .then(data => { if (data.success) setLearnerIds(data.learner_ids || []); })
      .catch(() => {});
  }, [lesson.template_id, token]);

  const handleToggle = (userId) => {
    setLearnerIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${API_URL}/admin/lessons/${lesson.template_id}/learners`, {
      method: 'POST', headers,
      body: JSON.stringify({ learner_ids: learnerIds }),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(); }, 1000);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '24px',
        width: '400px', maxHeight: '80vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 700 }}>🎓 Assign Learners</h3>
        <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#64748b' }}>{lesson.name}</p>
        
        {learnerUsers.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '16px' }}>
            No learners found. Add users with the learner role first.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
            {learnerUsers.map(user => (
              <div key={user.user_id}
                onClick={() => handleToggle(user.user_id)}
                style={{
                  padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  border: `2px solid ${learnerIds.includes(user.user_id) ? '#6366f1' : '#e2e8f0'}`,
                  background: learnerIds.includes(user.user_id) ? '#eef2ff' : '#f8fafc',
                }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                  background: learnerIds.includes(user.user_id) ? '#6366f1' : '#f1f5f9',
                  border: `2px solid ${learnerIds.includes(user.user_id) ? '#6366f1' : '#cbd5e1'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {learnerIds.includes(user.user_id) && (
                    <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>✓</span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>{user.name || user.email}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{user.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '10px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Saving...' : saved ? '✅ Saved!' : '💾 Save'}
          </button>
          <button onClick={onClose}
            style={{ padding: '10px 16px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function DistributorTeamPanel({ templateId, token }) {
  const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  const [team, setTeam] = useState({});
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!templateId || !token) return;
    Promise.all([
      fetch(`${API_URL}/lesson/templates/${templateId}/assigned-team`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()),
      fetch(`${API_URL}/lesson/templates/${templateId}/review/status`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()),
    ]).then(([teamData, statusData]) => {
      if (teamData.success) setTeam(teamData.team);
      if (statusData.success) setStatus(statusData.review?.status);
    }).catch(() => {});
  }, [templateId, token]);

  const ROLE_COLORS = {
    creator: '#6366f1', reviewer: '#f59e0b',
    final_approver: '#10b981', distributor: '#3b82f6',
  };

  const STATUS_LABELS = {
    draft: { label: 'Draft', color: '#94a3b8' },
    in_review: { label: 'In Review', color: '#f59e0b' },
    changes_requested: { label: 'Changes Requested', color: '#ef4444' },
    final_review: { label: 'Final Review', color: '#8b5cf6' },
    approved: { label: 'Approved', color: '#10b981' },
    deployed: { label: 'Deployed', color: '#3b82f6' },
  };

  const statusInfo = STATUS_LABELS[status] || STATUS_LABELS.draft;

  return (
    <div style={{
      width: '240px', minWidth: '240px', height: '100%',
      background: '#f8fafc', borderLeft: '1px solid #e2e8f0',
      display: 'flex', flexDirection: 'column', fontSize: '13px',
    }}>
      <div style={{
        padding: '14px 16px', borderBottom: '1px solid #e2e8f0',
        background: '#fff', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>Lesson Info</span>
        <span style={{
          fontSize: '11px', fontWeight: 600, color: '#fff',
          padding: '2px 8px', borderRadius: '20px',
          background: statusInfo.color,
        }}>
          {statusInfo.label}
        </span>
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>
          Team
        </div>
        {Object.keys(team).length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '12px' }}>No team assigned</div>
        ) : (
          Object.entries(team).map(([teamRole, members]) =>
            members.map(member => (
              <div key={member.user_id} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '8px',
              }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: ROLE_COLORS[teamRole] || '#6366f1',
                  color: '#fff', fontSize: '11px', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {(member.name || member.email || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                    {member.name || member.email?.split('@')[0]}
                  </div>
                  <div style={{ fontSize: '10px', color: ROLE_COLORS[teamRole] || '#6366f1', fontWeight: 600, textTransform: 'capitalize' }}>
                    {teamRole.replace('_', ' ')}
                  </div>
                </div>
              </div>
            ))
          )
        )}
      </div>
    </div>
  );
}

export default App;
